// src/components/integrations/ExplorePlugins.tsx
import React, { useState, useEffect} from "react";
import {
    Box,
    Breadcrumbs,
    Button,
    Container,
    Fade,
    Grid,
    Link,
    Typography,
    TextField,
    InputAdornment,
    Modal,
    Divider,
    IconButton,
    Paper,
    useTheme,
    // Alert, // No longer needed here
    // Snackbar, // No longer needed here
    // Uncomment if you use theme for transitions/styles
    // useTheme
} from "@mui/material";
import { Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import CloseIcon from "@mui/icons-material/Close";
import { useAuth } from "../../helpers/AuthProvider";
// --- REMOVED useLocation ---
import { useNavigate } from 'react-router-dom'; // Keep useNavigate
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import queryGraphql from "../../helpers/ApiInterface";
import MiniBot from "../MiniBot";
import MainDrawer, { openDrawerWidth, collapsedDrawerWidth } from "../MainDrawer"; // Adjust path if needed
// Plugin categories (remains the same)
const categories = [
    "All", "CI/CD Tools", "Cost Agents", "Git Providers", "Kubernetes Stack",
    "Observability", "Security", "API Management"
];

// Sample plugin data (remains the same)
const pluginsData = [
    { id: 1, name: "ArgoCD", category: "CI/CD Tools", description: "A declarative GitOps continuous delivery tool for Kubernetes.", image: "/assets/ArgoCD.svg" },
    { id: 2, name: "Jenkins", category: "CI/CD Tools", description: "An open-source automation server for building, testing, and deploying software (CI/CD).", image: "/assets/Jenkins.svg" },
    { id: 3, name: "GCP Cost Agent", category: "Cost Agents", description: "Tracks and reports Google Cloud Platform costs.", image: "/assets/gcp.svg" },
    { id: 4, name: "AWS Cost Agent", category: "Cost Agents", description: "Tracks and reports Amazon Web Services costs", image: "/assets/aws.svg" },
    { id: 5, name: "Azure Cost Agent", category: "Cost Agents", description: "Tracks and reports Microsoft Azure costs", image: "/assets/Azure.svg" },
    { id: 6, name: "Github", category: "Git Providers", description: "A popular web-based platform for Git version control hosting, collaboration, and CI/CD.", image: "/assets/GitHub.svg" },
    { id: 7, name: "Gitlab", category: "Git Providers", description: "A complete DevOps platform offering Git repository management, CI/CD, and more in a single application.", image: "/assets/Gitlab.svg" },
    { id: 8, name: "Bitbucket", category: "Git Providers", description: "A Git-based source code repository hosting service by Atlassian, often used with Jira.", image: "/assets/bit-bucket.svg" },
    { id: 9, name: "Istio", category: "Kubernetes Stack", description: "An open platform service mesh for connecting, securing, controlling, and observing microservices on Kubernetes.", image: "/assets/istio.svg" },
    { id: 10, name: "Kubernetes", category: "Kubernetes Stack", description: "An open-source system for automating deployment, scaling, and management of containerized applications.", image: "/assets/kubernetes.svg" },
    { id: 11, name: "Grafana", category: "Observability", description: "An open-source platform for visualizing, querying, and alerting on metrics and logs.", image: "/assets/Grafana.svg" },
    { id: 12, name: "Prometheus", category: "Observability", description: "An open-source monitoring and alerting toolkit focused on time-series data collection.", image: "/assets/Prometheus.svg" },
    { id: 13, name: "CheckOV", category: "Security", description: "Infrastructure as Code (IaC) static analysis tool for security and compliance.", image: "/assets/checkov.svg" },
    { id: 14, name: "Kyverno", category: "Security", description: "Kubernetes native policy management for security and compliance.", image: "/assets/kyverno.svg" },
    { id: 15, name: "Mirroring App", category: "Git Providers", description: "Helps you mirror existing repos to intellicore gitops workflow", image: "/assets/flip-horizontal-line.svg" },
    { id: 16, name: "SonarQube", category: "Security", description: "An open platform for continuous inspection of code quality and security.", image: "/assets/sonarqube.svg" },
    // { id: 17, name: "AWS Cloud Logging", category: "API Management", description: "Centralize, monitor, and analyze log data from AWS services.", image: "/assets/aws-cloud-monitoring.svg" },
    // { id: 18, name: "XRAY", category: "API Management", description: "Analyze and debug production, distributed applications, such as those built using a microservices architecture.", image: "/assets/aws-xray.svg" },
    { id: 19, name: "Custom API Catalogue", category: "API Management", description: "Manage and discover your APIs in a centralized catalogue.", image: "/assets/aws-cloud-monitoring.svg" },
];

// Section Headings (remains the same)
const sectionHeadings: Record<string, string> = {
    "Cost Agents": "Cost Optimization Tools", 
    "CI/CD Tools": "CI/CD Automation",
    "Git Providers": "Version Control Integrations", 
    "Kubernetes Stack": "Container Orchestration Solutions",
    "Security": "Security and Compliance",
    "Observability": "Monitoring and Visualization Tools",
    "API Management": "API Monitoring and Tracing",
};

// API Key to Plugin Name mapping (remains the same)
const apiKeyToPluginName: Record<string, string> = {
    costAgent: "GCP Cost Agent", kyverno: "Kyverno", checkov: "CheckOV", prometheus: "Prometheus", grafana: "Grafana", mirroringApp: "Mirroring App", sonarQube:"SonarQube", awsCloudLogging: "AWS Cloud Logging", xray: "XRAY", customAPICatalogue: "Custom API Catalogue",

};

// Modal Style (remains the same)
const modalStyle = {
    position: "absolute" as "absolute", top: "50%", left: "50%",
    transform: "translate(-50%, -50%)", width: 700, maxWidth: '90vw', maxHeight: '90vh',
    bgcolor: "white", border: "0px", boxShadow: 24, borderRadius: "16px", outline: 'none',
    display: 'flex', flexDirection: 'column',
};


// --- Main Component ---
const ExplorePluginsContent: React.FC = () => {
    const navigate = useNavigate();
    // --- State (Snackbar state removed) ---
    const [installationStatuses, setInstallationStatuses] = useState<Record<string, boolean>>({});
    const [isLoadingStatuses, setIsLoadingStatuses] = useState<boolean>(true);
    const [search, setSearch] = useState("");
    const { user } = useAuth();
    const canInstallPlugins = user?.roles?.some(
        (role: string) => role === "idp_admin" 
    ) || false;

    const isViewer =
        user.roles?.some((role: string) => role === "viewer") || false;
    const [selectedCategory, setSelectedCategory] = useState("All");
    const [openModal, setOpenModal] = useState(false);
    const [selectedPlugin, setSelectedPlugin] = useState<string | null>(null);
    const [grafanaStatus, setGrafanaStatus] = useState<any>(null);
    useEffect(() => {
        let isMounted = true;
        (async () => {
            try {
                const data = await queryGraphql(`
                    query GetIntegrationStatus {
                      getIntegrations {
                        grafana { enabled updatedAt hostedByFaraday }
                        sonarQube { enabled updatedAt hostedByFaraday }
                        id
                      }
                    }
                `);
                if (isMounted) {
                    if (data.getIntegrations) { setGrafanaStatus(data.getIntegrations); console.log("Fetched grafana:", data.getIntegrations); }
                    else { console.log("No integration data returned."); setGrafanaStatus(null); }
                }
            } catch (err) { console.error("Err fetching integrations:", err);  if (isMounted) setGrafanaStatus(null); }
            
        })();
        return () => { isMounted = false };
     }, []);
     const isGrafanaInstalled = grafanaStatus?.grafana?.enabled;
     const isGrafanaFaraday = grafanaStatus?.grafana?.hostedByFaraday;
    // --- Fetch Installation Statuses (No changes) ---
    const fetchInstallationStatuses = async (): Promise<Record<string, boolean>> => {
        console.log("Fetching installation statuses from API...");
        const graphqlQuery = ` query Query { getIntegrations { costAgent { enabled } kyverno { enabled } checkov { enabled } prometheus { enabled } grafana {enabled} mirroringApp {enabled} sonarQube {enabled}  awsCloudLogging { enabled } xray { enabled } customAPICatalogue { enabled } id }  } `;
        try {
            const response = await queryGraphql(graphqlQuery);
            console.log("LOG 1: Raw API Response:", JSON.stringify(response, null, 2));
            const integrationsData = response?.getIntegrations;
            const statuses: Record<string, boolean> = {};
            if (integrationsData) {
                console.log("LOG 2: Parsed integrationsData:", integrationsData);
                for (const apiKey in apiKeyToPluginName) {
                    const pluginName = apiKeyToPluginName[apiKey];
                    const isEnabledInApi = integrationsData[apiKey]?.enabled === true;
                    statuses[pluginName] = isEnabledInApi;
                    console.log(`LOG 3: Checking API key: '${apiKey}' -> Plugin Name: '${pluginName}'. API value:`, integrationsData[apiKey], ` API enabled: ${integrationsData[apiKey]?.enabled}, Setting status: ${isEnabledInApi}`);
                }
            } else {
                 console.warn("No integration data found. Defaulting mapped plugins to false.");
                 for (const apiKey in apiKeyToPluginName) { statuses[apiKeyToPluginName[apiKey]] = false; }
            }
            console.log("LOG 4: Final statuses object to be set:", statuses);
            return statuses;
        } catch (error) {
            console.error("Error fetching or processing integration statuses:", error);
             const statuses: Record<string, boolean> = {};
             for (const apiKey in apiKeyToPluginName) { statuses[apiKeyToPluginName[apiKey]] = false; }
            return statuses;
        }
    };

    // --- useEffect to Fetch Statuses on Mount (No changes) ---
    useEffect(() => {
        let isMounted = true;
        console.log("useEffect running to fetch statuses...");
        setIsLoadingStatuses(true);
        fetchInstallationStatuses()
            .then(statuses => { if (isMounted) { setInstallationStatuses(statuses); } })
            .catch(error => { console.error("Error setting installation statuses:", error); })
            .finally(() => { if (isMounted) { setIsLoadingStatuses(false); } });
        return () => { isMounted = false; };
    }, []);

    // --- useEffect for Snackbar REMOVED ---

    // --- Refactored installHandlers (MODIFIED ONSUBMIT) ---
    const installHandlers = {
        "GCP Cost Agent": () => (
            <Formik
                initialValues={{projectId: "", datasetId: "", tableId: "" }}
                validationSchema={Yup.object({
                    projectId: Yup.string().required("Project ID is required").matches( /^[a-zA-Z0-9_-]+$/,"Invalid Project ID format"),
                    datasetId: Yup.string().required("Dataset ID is required").matches( /^[a-zA-Z0-9_-]+$/,"Invalid Dataset ID format"),
                    tableId: Yup.string().required("Table ID is required").matches( /^[a-zA-Z0-9_-]+$/,"Invalid Table ID format"),
                })}
                onSubmit={async (values, { setSubmitting, setFieldError }) => {
                    const pluginName = "GCP Cost Agent"; // Define for reuse
                    console.log(`${pluginName} Submitted Data:`, values);
                    setSubmitting(true);
                    //setFieldError('general', null); // Clear previous errors
                    try {
                        const data = await queryGraphql(
                            ` mutation Mutation($input: UpdateIntegrationsInput) { updateIntegration(input: $input) { costAgent { enabled } } } `,
                            { "input": { costAgent: { datasetId: values.datasetId, enabled: true, projectId: values.projectId, tableId: values.tableId } } }
                        );

                        if (data?.updateIntegration?.costAgent?.enabled) {
                            console.log(`${pluginName} installation successful via API.`);
                            setInstallationStatuses(prevStatuses => ({ ...prevStatuses, [pluginName]: true }));
                            handleCloseModal(); // Close modal *before* navigating
                            // --- *** REDIRECT ON SUCCESS *** ---
                            navigate('/integrations', { state: { installedPlugin: pluginName } });
                        } else {
                            console.error(`${pluginName} installation API call did not confirm success:`, data);
                            setFieldError('general', `Failed to install ${pluginName}. API did not confirm success.`); // Show error in modal
                        }
                    } catch (error) {
                        console.error(`Error submitting ${pluginName} form:`, error);
                        setFieldError('general', `An error occurred during installation. Please check details and try again.`); // Show error in modal
                    } finally {
                        setSubmitting(false);
                    }
                }}
            >
                {({ handleChange, handleBlur, values, errors, touched, isSubmitting }) => ( // Added isSubmitting
                    <Form>
                        {/* Service Account Display */}
                        <Box px={2.5} pt={1} pb={2}>
                           <Typography fontFamily="Gilroy-Regular" fontSize="14px" color="gray" gutterBottom>Grant Permissions to Service Account</Typography>
                           <Box sx={{ p: 1, bgcolor: "#F5F5F5", borderRadius: "8px", fontFamily: "monospace", fontSize: "14px", wordBreak: "break-all", border: '1px solid #E0E0E0' }}>
                                <Typography component="span" sx={{ fontFamily: 'monospace', fontSize: '14px', color: '#333' }}>gcp-cost-agent@your-project.iam.gserviceaccount.com</Typography>
                           </Box>
                       </Box>
                       {/* Input Fields */}
                       <Box px={2.5} pb={2}>
                           <Grid container spacing={2.5}>
                               {/* Project ID, Dataset ID, Table ID Fields (styles unchanged) */}
                               <Grid item xs={12} md={4}>
                                   <TextField fullWidth variant="outlined" name="projectId" label="Project ID" value={values.projectId} onChange={handleChange} onBlur={handleBlur} error={!!errors.projectId && touched.projectId} helperText={touched.projectId ? errors.projectId : ""} sx={{ /* Your TextField Styles */ }} />
                               </Grid>
                               <Grid item xs={12} md={4}>
                                   <TextField fullWidth variant="outlined" name="datasetId" label="Dataset ID" value={values.datasetId} onChange={handleChange} onBlur={handleBlur} error={!!errors.datasetId && touched.datasetId} helperText={touched.datasetId ? errors.datasetId : ""} sx={{ /* Your TextField Styles */ }} />
                               </Grid>
                               <Grid item xs={12} md={4}>
                                   <TextField fullWidth variant="outlined" name="tableId" label="Table ID" value={values.tableId} onChange={handleChange} onBlur={handleBlur} error={!!errors.tableId && touched.tableId} helperText={touched.tableId ? errors.tableId : ""} sx={{ /* Your TextField Styles */ }} />
                               </Grid>
                           </Grid>
                           {/* General Error Display */}
                            {/* {errors.general && (
                                <Typography color="error" variant="body2" sx={{fontFamily: 'Gilroy-Regular', pt: 1.5, pl: 0.5}}>
                                    {errors.general}
                                </Typography>
                            )} */}
                       </Box>
                       <Divider sx={{ mt: 2 }} />
                       {/* Action Button */}
                       <Box sx={{ p: 2.5, textAlign: "right" }}>
                           <Button
                               sx={{ /* Your Button Styles */ backgroundColor: "black", color: "white", fontFamily: "Gilroy-Bold", fontSize: "14px", borderRadius: "8px", textTransform: "none", px: 4, transition: 'background-color 0.3s ease', '&:hover': { backgroundColor: "dimgray" }, '&:disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)', cursor: 'not-allowed' }, }}
                               variant="contained" size="small" type="submit"
                               disabled={isSubmitting}
                           >
                               {isSubmitting ? 'Installing...' : 'Install'}
                           </Button>
                       </Box>
                   </Form>
                )}
            </Formik>
        ),

        "CheckOV": () => (
            <Formik
                initialValues={{}}
                onSubmit={async (values, { setSubmitting, setFieldError }) => {
                    const pluginName = "CheckOV"; // Define for reuse
                    console.log(`Enabling ${pluginName}...`);
                    setSubmitting(true);
                    //setFieldError('general', null);
                    const ENABLE_CHECKOV_MUTATION = ` mutation Mutation($input: UpdateIntegrationsInput) { updateIntegration(input: $input) { checkov { enabled } } } `;
                    try {
                        const result = await queryGraphql( ENABLE_CHECKOV_MUTATION, { "input": { checkov: { enabled: true } } } );
                        console.log(`${pluginName} Enable Result:`, result);
                        if (result?.updateIntegration?.checkov?.enabled) {
                            console.log(`${pluginName} enabled successfully.`);
                            setInstallationStatuses(prevStatuses => ({ ...prevStatuses, [pluginName]: true }));
                            handleCloseModal(); // Close modal *before* navigating
                            // --- *** REDIRECT ON SUCCESS *** ---
                            navigate('/integrations', { state: { installedPlugin: pluginName } });
                        } else {
                            console.error(`${pluginName} enabling failed:`, result);
                            setFieldError('general', `Failed to enable ${pluginName}. API did not confirm success.`);
                        }
                    } catch (err) {
                        console.error(`Failed to enable ${pluginName}:`, err);
                        setFieldError('general', `Failed to enable ${pluginName}. Please try again.`);
                    } finally {
                        setSubmitting(false);
                    }
                }}
            >
                {({ isSubmitting, errors }) => (
                    <Form>
                        <Box p={2}>
                            <Typography sx={{ mb: 2, fontFamily: 'Gilroy-Regular' }}>Click on the button below to Enable CheckOV</Typography>
                            {/* General Error Display */}
                            {/* {errors.general && ( <Typography color="error" variant="body2" sx={{fontFamily: 'Gilroy-Regular', pb: 1}}> {errors.general} </Typography> )} */}
                            <Box>
                            <Typography sx={{ mb: 2, fontFamily: 'Gilroy-Regular' , color:'red'}}>Note: Once you onboard a resource after enabling CheckOV, you will not be able to disable it.</Typography>
                            </Box>
                            <Divider sx={{ mt: 0 }} />
                            {/* Button container */}
                            <Box sx={{ paddingTop: 2.5,  textAlign: "right" }}>
                                <Button
                                    type="submit"
                                    sx={{ /* Your Button Styles */ backgroundColor: "black", color: "white", fontFamily: "Gilroy-Bold", fontSize: "14px", borderRadius: "8px", textTransform: "none", px: 4, transition: 'background-color 0.3s ease', '&:hover': { backgroundColor: "dimgray" }, '&:disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)', cursor: 'not-allowed' }, }}
                                    variant="contained" size="small"
                                    disabled={isSubmitting}
                                > {isSubmitting ? 'Enabling...' : 'Enable'} </Button>
                            </Box>
                        </Box>
                    </Form>
                )}
            </Formik>
        ),

        "Kyverno": () =>(
            <Formik
                initialValues={{}}
                onSubmit={async (values, { setSubmitting, setFieldError }) => {
                    const pluginName = "Kyverno"; // Define for reuse
                    console.log(`Enabling ${pluginName}...`);
                    setSubmitting(true);
                    //setFieldError('general', null);
                    const ENABLE_KYVERNO_MUTATION = ` mutation Mutation($input: UpdateIntegrationsInput) { updateIntegration(input: $input) { kyverno { enabled } } } `;
                    try {
                        const result = await queryGraphql( ENABLE_KYVERNO_MUTATION, { "input": { kyverno: { enabled: true } } } );
                        console.log(`${pluginName} Enable Result:`, result);
                        if (result?.updateIntegration?.kyverno?.enabled) {
                            console.log(`${pluginName} enabled successfully.`);
                            setInstallationStatuses(prevStatuses => ({ ...prevStatuses, [pluginName]: true }));
                            handleCloseModal(); // Close modal *before* navigating
                             // --- *** REDIRECT ON SUCCESS *** ---
                            navigate('/integrations', { state: { installedPlugin: pluginName } });
                        } else {
                            console.error(`${pluginName} enabling failed:`, result);
                            setFieldError('general', `Failed to enable ${pluginName}. API did not confirm success.`);
                        }
                    } catch (err) {
                        console.error(`Failed to enable ${pluginName}:`, err);
                        setFieldError('general', `Failed to enable ${pluginName}. Please try again.`);
                    } finally {
                        setSubmitting(false);
                    }
                }}
            >
                {({ isSubmitting, errors }) => (
                    <Form>
                        <Box p={2}>
                            <Typography sx={{ mb: 2, fontFamily: 'Gilroy-Regular' }}>Click on the button below to Enable Kyverno</Typography>
                            {/* General Error Display */}
                            {/* {errors.general && ( <Typography color="error" variant="body2" sx={{fontFamily: 'Gilroy-Regular', pb: 1}}> {errors.general} </Typography> )} */}
                            <Box>
                            <Typography sx={{ mb: 2, fontFamily: 'Gilroy-Regular' , color:'red'}}>Note: Once you onboard a resource after enabling Kyverno, you will not be able to disable it.</Typography>
                            </Box>
                            <Divider sx={{ mt: 0 }} />
                            {/* Button container */}
                            <Box sx={{  paddingTop: 2.5, textAlign: "right" }}>
                                <Button
                                    type="submit"
                                    sx={{ /* Your Button Styles */ backgroundColor: "black", color: "white", fontFamily: "Gilroy-Bold", fontSize: "14px", borderRadius: "8px", textTransform: "none", px: 4, transition: 'background-color 0.3s ease', '&:hover': { backgroundColor: "dimgray" }, '&:disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)', cursor: 'not-allowed' }, }}
                                    variant="contained" size="small"
                                    disabled={isSubmitting}
                                > {isSubmitting ? 'Enabling...' : 'Enable'} </Button>
                            </Box>
                        </Box>
                    </Form>
                )}
            </Formik>
        ),
        "SonarQube": () =>(
            <Formik
                initialValues={{}}
                onSubmit={async (values, { setSubmitting, setFieldError }) => {
                    const pluginName = "SonarQube"; // Define for reuse
                    console.log(`Enabling ${pluginName}...`);
                    setSubmitting(true);
                    //setFieldError('general', null);
                    const ENABLE_SONARQUBE_MUTATION = ` mutation Mutation($input: UpdateIntegrationsInput) { updateIntegration(input: $input) { sonarQube { enabled } } } `;
                    try {
                        const result = await queryGraphql( ENABLE_SONARQUBE_MUTATION, { "input": { sonarQube: { enabled: true } } } );
                        console.log(`${pluginName} Enable Result:`, result);
                        if (result?.updateIntegration?.sonarQube?.enabled) {
                            console.log(`${pluginName} enabled successfully.`);
                            setInstallationStatuses(prevStatuses => ({ ...prevStatuses, [pluginName]: true }));
                            handleCloseModal(); // Close modal *before* navigating
                             // --- *** REDIRECT ON SUCCESS *** ---
                            navigate('/integrations', { state: { installedPlugin: pluginName } });
                        } else {
                            console.error(`${pluginName} enabling failed:`, result);
                            setFieldError('general', `Failed to enable ${pluginName}. API did not confirm success.`);
                        }
                    } catch (err) {
                        console.error(`Failed to enable ${pluginName}:`, err);
                        setFieldError('general', `Failed to enable ${pluginName}. Please try again.`);
                    } finally {
                        setSubmitting(false);
                    }
                }}
            >
                {({ isSubmitting, errors }) => (
                    <Form>
                        <Box p={2}>
                            <Typography sx={{ mb: 2, fontFamily: 'Gilroy-Regular' }}>Click on the button below to Enable SonarQube</Typography>
                            {/* General Error Display */}
                            {/* {errors.general && ( <Typography color="error" variant="body2" sx={{fontFamily: 'Gilroy-Regular', pb: 1}}> {errors.general} </Typography> )} */}
                            {/* <Box>
                            <Typography sx={{ mb: 2, fontFamily: 'Gilroy-Regular' , color:'red'}}>Note: Once you onboard a resource after enabling Kyverno, you will not be able to disable it.</Typography>
                            </Box> */}
                            <Divider sx={{ mt: 0 }} />
                            {/* Button container */}
                            <Box sx={{  paddingTop: 2.5, textAlign: "right" }}>
                                <Button
                                    type="submit"
                                    sx={{ /* Your Button Styles */ backgroundColor: "black", color: "white", fontFamily: "Gilroy-Bold", fontSize: "14px", borderRadius: "8px", textTransform: "none", px: 4, transition: 'background-color 0.3s ease', '&:hover': { backgroundColor: "dimgray" }, '&:disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)', cursor: 'not-allowed' }, }}
                                    variant="contained" size="small"
                                    disabled={isSubmitting}
                                > {isSubmitting ? 'Enabling...' : 'Enable'} </Button>
                            </Box>
                        </Box>
                    </Form>
                )}
            </Formik>
        ),


        "Mirroring App": () => (
            <Formik
                initialValues={{}}
                onSubmit={async (values, { setSubmitting, setFieldError }) => {
                    const pluginName = "Mirroring App"; // Define for reuse
                    console.log(`Enabling ${pluginName}...`);
                    setSubmitting(true);
                    //setFieldError('general', null);
                    const ENABLE_MIRRORINGAPP_MUTATION = ` mutation Mutation($input: UpdateIntegrationsInput) { updateIntegration(input: $input) { mirroringApp { enabled } } } `;
                    try {
                        const result = await queryGraphql( ENABLE_MIRRORINGAPP_MUTATION, { "input": { mirroringApp: { enabled: true } } } );
                        console.log(`${pluginName} Enable Result:`, result);
                        if (result?.updateIntegration?.mirroringApp?.enabled) {
                            console.log(`${pluginName} enabled successfully.`);
                            setInstallationStatuses(prevStatuses => ({ ...prevStatuses, [pluginName]: true }));
                            handleCloseModal(); // Close modal *before* navigating
                            // --- *** REDIRECT ON SUCCESS *** ---
                            navigate('/integrations', { state: { installedPlugin: pluginName } });
                        } else {
                            console.error(`${pluginName} enabling failed:`, result);
                            setFieldError('general', `Failed to enable ${pluginName}. API did not confirm success.`);
                        }
                    } catch (err) {
                        console.error(`Failed to enable ${pluginName}:`, err);
                        setFieldError('general', `Failed to enable ${pluginName}. Please try again.`);
                    } finally {
                        setSubmitting(false);
                    }
                }}
            >
                {({ isSubmitting, errors }) => (
                    <Form>
                        <Box p={2}>
                            <Typography sx={{ mb: 2, fontFamily: 'Gilroy-Regular' }}>Click on the button below to Enable Mirroring App</Typography>
                            {/* General Error Display */}
                            {/* {errors.general && ( <Typography color="error" variant="body2" sx={{fontFamily: 'Gilroy-Regular', pb: 1}}> {errors.general} </Typography> )} */}
                            <Box>
                            <Typography sx={{ mb: 2, fontFamily: 'Gilroy-Regular' , color:'red'}}>Note: Once enabled you will not be able to disable it.</Typography>
                            </Box>
                            <Divider sx={{ mt: 0 }} />
                            {/* Button container */}
                            <Box sx={{ paddingTop: 2.5,  textAlign: "right" }}>
                                <Button
                                    type="submit"
                                    sx={{ /* Your Button Styles */ backgroundColor: "black", color: "white", fontFamily: "Gilroy-Bold", fontSize: "14px", borderRadius: "8px", textTransform: "none", px: 4, transition: 'background-color 0.3s ease', '&:hover': { backgroundColor: "dimgray" }, '&:disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)', cursor: 'not-allowed' }, }}
                                    variant="contained" size="small"
                                    disabled={isSubmitting}
                                > {isSubmitting ? 'Enabling...' : 'Enable'} </Button>
                            </Box>
                        </Box>
                    </Form>
                )}
            </Formik>
        ),


        // Other handlers remain simple placeholders
        "AWS Cost Agent": () => <Box p={2}><Typography>Coming Soon!!</Typography></Box>,
        "Azure Cost Agent": () => <Box p={2}><Typography>Coming Soon!!</Typography></Box>,
        "Gitlab": () => <Box p={2}><Typography>Coming Soon!!</Typography></Box>,
        "Github": () => <Box p={2}><Typography>Coming Soon!!</Typography></Box>,
        "Bitbucket": () => <Box p={2}><Typography>Coming Soon!!</Typography></Box>,
        "Istio": () => <Box p={2}><Typography>Coming Soon!!</Typography></Box>,
        "Kubernetes": () => <Box p={2}><Typography>Coming Soon!!</Typography></Box>,
        "Grafana": () => (
        <Formik
            initialValues={{}}
            onSubmit={async (values, { setSubmitting, setFieldError }) => {
                const pluginName = "Grafana"; // Define for reuse
                console.log(`Enabling ${pluginName}...`);
                setSubmitting(true);
                //setFieldError('general', null);
                const ENABLE_GRAFANA_MUTATION = ` mutation Mutation($input: UpdateIntegrationsInput) { updateIntegration(input: $input) { grafana { enabled } } } `;
                try {
                    const result = await queryGraphql( ENABLE_GRAFANA_MUTATION, { "input": { grafana: { enabled: true } } } );
                    console.log(`${pluginName} Enable Result:`, result);
                    if (result?.updateIntegration?.grafana?.enabled) {
                        console.log(`${pluginName} enabled successfully.`);
                        setInstallationStatuses(prevStatuses => ({ ...prevStatuses, [pluginName]: true }));
                        handleCloseModal(); // Close modal *before* navigating
                         // --- *** REDIRECT ON SUCCESS *** ---
                        navigate('/integrations', { state: { installedPlugin: pluginName } });
                    } else {
                        console.error(`${pluginName} enabling failed:`, result);
                        setFieldError('general', `Failed to enable ${pluginName}. API did not confirm success.`);
                    }
                } catch (err) {
                    console.error(`Failed to enable ${pluginName}:`, err);
                    setFieldError('general', `Failed to enable ${pluginName}. Please try again.`);
                } finally {
                    setSubmitting(false);
                }
            }}
        >
            {({ isSubmitting, errors }) => (
                <Form>
                    <Box p={2}>
                        <Typography sx={{ mb: 2, fontFamily: 'Gilroy-Regular' }}>Click on the button below to Enable Grafana</Typography>
                        {/* General Error Display */}
                        {/* {errors.general && ( <Typography color="error" variant="body2" sx={{fontFamily: 'Gilroy-Regular', pb: 1}}> {errors.general} </Typography> )} */}
                        {/* <Box>
                        <Typography sx={{ mb: 2, fontFamily: 'Gilroy-Regular' , color:'red'}}>Note: Once you onboard a resource after enabling Kyverno, you will not be able to disable it.</Typography>
                        </Box> */}
                        <Divider sx={{ mt: 0 }} />
                        {/* Button container */}
                        <Box sx={{  paddingTop: 2.5, textAlign: "right" }}>
                            <Button
                                type="submit"
                                sx={{ /* Your Button Styles */ backgroundColor: "black", color: "white", fontFamily: "Gilroy-Bold", fontSize: "14px", borderRadius: "8px", textTransform: "none", px: 4, transition: 'background-color 0.3s ease', '&:hover': { backgroundColor: "dimgray" }, '&:disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)', cursor: 'not-allowed' }, }}
                                variant="contained" size="small"
                                disabled={isSubmitting}
                            > {isSubmitting ? 'Enabling...' : 'Enable'} </Button>
                        </Box>
                    </Box>
                </Form>
            )}
        </Formik>
        ),
        "Prometheus": () => (
            <Formik
                initialValues={{}}
                onSubmit={async (values, { setSubmitting, setFieldError }) => {
                    const pluginName = "Prometheus"; // Define for reuse
                    console.log(`Enabling ${pluginName}...`);
                    setSubmitting(true);
                    //setFieldError('general', null);
                    const ENABLE_PROMETHEUS_MUTATION = ` mutation Mutation($input: UpdateIntegrationsInput) { updateIntegration(input: $input) { prometheus { enabled } } } `;
                    try {
                        const result = await queryGraphql( ENABLE_PROMETHEUS_MUTATION, { "input": { prometheus: { enabled: true } } } );
                        console.log(`${pluginName} Enable Result:`, result);
                        if (result?.updateIntegration?.prometheus?.enabled) {
                            console.log(`${pluginName} enabled successfully.`);
                            setInstallationStatuses(prevStatuses => ({ ...prevStatuses, [pluginName]: true }));
                            handleCloseModal(); // Close modal *before* navigating
                             // --- *** REDIRECT ON SUCCESS *** ---
                            navigate('/integrations', { state: { installedPlugin: pluginName } });
                        } else {
                            console.error(`${pluginName} enabling failed:`, result);
                            setFieldError('general', `Failed to enable ${pluginName}. API did not confirm success.`);
                        }
                    } catch (err) {
                        console.error(`Failed to enable ${pluginName}:`, err);
                        setFieldError('general', `Failed to enable ${pluginName}. Please try again.`);
                    } finally {
                        setSubmitting(false);
                    }
                }}
            >
                {({ isSubmitting, errors }) => (
                    <Form>
                        <Box p={2}>
                            <Typography sx={{ mb: 2, fontFamily: 'Gilroy-Regular' }}>Click on the button below to Enable Prometheus</Typography>
                            {(!isGrafanaInstalled || !isGrafanaFaraday) && (
                                <Box>
                                <Typography sx={{ mb: 2, fontFamily: 'Gilroy-Regular' , color:'red'}}>To enable Prometheus, you first need to enable Grafana.</Typography>
                                </Box>
                            )}
                            {/* Add a message if the user is not an admin and other conditions are met */}
                            {(isGrafanaInstalled && isGrafanaFaraday && !canInstallPlugins) && (
                                <Box>
                                    <Typography sx={{ mb: 2, fontFamily: 'Gilroy-Regular', color: 'orange' }}>
                                        Admin privileges are required to install Prometheus.
                                    </Typography>
                                </Box>
                            )}
                            <Divider sx={{ mt: 0 }} />
                            {/* Button container */}
                            <Box sx={{  paddingTop: 2.5, textAlign: "right" }}>
                                <Button
                                    type="submit"
                                    sx={{ /* Your Button Styles */ backgroundColor: "black", color: "white", fontFamily: "Gilroy-Bold", fontSize: "14px", borderRadius: "8px", textTransform: "none", px: 4, transition: 'background-color 0.3s ease', '&:hover': { backgroundColor: "dimgray" }, '&:disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)', cursor: 'not-allowed' }, }}
                                    variant="contained" size="small"
                                    // --- MODIFIED DISABLED LOGIC ---
                                    disabled={
                                        isSubmitting ||
                                        !isGrafanaInstalled ||
                                        !isGrafanaFaraday ||
                                        !canInstallPlugins // Add this condition
                                    }
                                > {isSubmitting ? 'Enabling...' : 'Enable'} </Button>
                            </Box>
                        </Box>
                    </Form>
                )}
            </Formik>
        ),
        "ArgoCD": () => <Box p={2}><Typography>Coming Soon!!</Typography></Box>,
        "Jenkins": () => <Box p={2}><Typography>Coming Soon!!</Typography></Box>,
        "AWS Cloud Logging": () => (
                        <Formik
                            initialValues={{}}
                            onSubmit={async (values, { setSubmitting, setFieldError }) => {
                                const pluginName = "AWSCloudLogging"; // Define for reuse
                                console.log(`Enabling ${pluginName}...`);
                                setSubmitting(true);
                                const ENABLE_AWSCLOUDLOGGING_MUTATION = ` mutation Mutation($input: UpdateIntegrationsInput) { updateIntegration(input: $input) { awsCloudLogging { enabled } } } `;
                                try {
                                    const result = await queryGraphql( ENABLE_AWSCLOUDLOGGING_MUTATION, { "input": { awsCloudLogging: { enabled: true } } } );
                                    console.log(`${pluginName} Enable Result:`, result);
                                    if (result?.updateIntegration?.awsCloudLogging?.enabled) {
                                        console.log(`${pluginName} enabled successfully.`);
                                        setInstallationStatuses(prevStatuses => ({ ...prevStatuses, [pluginName]: true }));
                                        handleCloseModal(); // Close modal before navigating
                                        navigate('/integrations', { state: { installedPlugin: pluginName } });
                                    } else {
                                        console.error(`${pluginName} enabling failed:`, result);
                                        setFieldError('general', `Failed to enable ${pluginName}. API did not confirm success.`);
                                    }
                                } catch (err) {
                                    console.error(`Failed to enable ${pluginName}:`, err);
                                    setFieldError('general', `Failed to enable ${pluginName}. Please try again.`);
                                } finally {
                                    setSubmitting(false);
                                }
                            }}
                        >
                            {({ isSubmitting }) => (
                                <Form>
                                    <Box p={2}>
                                        <Typography sx={{ mb: 2, fontFamily: 'Gilroy-Regular' }}>Click on the button below to Enable AWS Cloud Logging</Typography>
                                        <Divider sx={{ mt: 0 }} />
                                        {/* Button container */}
                                        <Box sx={{  paddingTop: 2.5, textAlign: "right" }}>
                                            <Button
                                                type="submit"
                                                sx={{ backgroundColor: "black", color: "white", fontFamily: "Gilroy-Bold", fontSize: "14px", borderRadius: "8px", textTransform: "none", px: 4, transition: 'background-color 0.3s ease', '&:hover': { backgroundColor: "dimgray" }, '&:disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)', cursor: 'not-allowed' }, }}
                                                variant="contained" size="small"
                                                disabled={isSubmitting}
                                            >
                                                {isSubmitting ? 'Enabling...' : 'Enable'}
                                            </Button>
                                        </Box>
                                    </Box>
                                </Form>
                            )}
                        </Formik>
                    ),
        "XRAY": () => (
                        <Formik
                            initialValues={{}}
                            onSubmit={async (values, { setSubmitting, setFieldError }) => {
                                const pluginName = "XRAY"; // Define for reuse
                                console.log(`Enabling ${pluginName}...`);
                                setSubmitting(true);
                                const ENABLE_XRAY_MUTATION = ` mutation Mutation($input: UpdateIntegrationsInput) { updateIntegration(input: $input) { xray { enabled } } } `;
                                try {
                                    const result = await queryGraphql( ENABLE_XRAY_MUTATION, { "input": { xray: { enabled: true } } } );
                                    console.log(`${pluginName} Enable Result:`, result);
                                    if (result?.updateIntegration?.xray?.enabled) {
                                        console.log(`${pluginName} enabled successfully.`);
                                        setInstallationStatuses(prevStatuses => ({ ...prevStatuses, [pluginName]: true }));
                                        handleCloseModal(); // Close modal before navigating
                                        navigate('/integrations', { state: { installedPlugin: pluginName } });
                                    } else {
                                        console.error(`${pluginName} enabling failed:`, result);
                                        setFieldError('general', `Failed to enable ${pluginName}. API did not confirm success.`);
                                    }
                                } catch (err) {
                                    console.error(`Failed to enable ${pluginName}:`, err);
                                    setFieldError('general', `Failed to enable ${pluginName}. Please try again.`);
                                } finally {
                                    setSubmitting(false);
                                }
                            }}
                        >
                            {({ isSubmitting }) => (
                                <Form>
                                    <Box p={2}>
                                        <Typography sx={{ mb: 2, fontFamily: 'Gilroy-Regular' }}>Click on the button below to Enable X-Ray</Typography>
                                        <Divider sx={{ mt: 0 }} />
                                        {/* Button container */}
                                        <Box sx={{  paddingTop: 2.5, textAlign: "right" }}>
                                            <Button
                                                type="submit"
                                                sx={{ backgroundColor: "black", color: "white", fontFamily: "Gilroy-Bold", fontSize: "14px", borderRadius: "8px", textTransform: "none", px: 4, transition: 'background-color 0.3s ease', '&:hover': { backgroundColor: "dimgray" }, '&:disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)', cursor: 'not-allowed' }, }}
                                                variant="contained" size="small"
                                                disabled={isSubmitting}
                                            >
                                                {isSubmitting ? 'Enabling...' : 'Enable'}
                                            </Button>
                                        </Box>
                                    </Box>
                                </Form>
                            )}
                        </Formik>
                    ),
                    "Custom API Catalogue": () => (
 <Formik
                 initialValues={{}}
                    onSubmit={async (values, { setSubmitting, setFieldError }) => {
                     const pluginName = "Custom API Catalogue"; // Define for reuse
                    console.log(`Enabling ${pluginName}...`);
                    setSubmitting(true);
                        const ENABLE_CUSTOMAPICATALOGUE_MUTATION = ` mutation Mutation($input: UpdateIntegrationsInput) { updateIntegration(input: $input) { customAPICatalogue { enabled } } } `;
                            try {
                    const result = await queryGraphql( ENABLE_CUSTOMAPICATALOGUE_MUTATION, { "input": { customAPICatalogue: { enabled: true } } } );
                    console.log(`${pluginName} Enable Result:`, result);
                     if (result?.updateIntegration?.customAPICatalogue?.enabled) {
                     console.log(`${pluginName} enabled successfully.`);
                    setInstallationStatuses(prevStatuses => ({ ...prevStatuses, [pluginName]: true }));
                    handleCloseModal(); // Close modal before navigating
                    navigate('/integrations', { state: { installedPlugin: pluginName } });
                     } else {
                     console.error(`${pluginName} enabling failed:`, result);
                    setFieldError('general', `Failed to enable ${pluginName}. API did not confirm success.`);
                     }
                     } catch (err) {
                     console.error(`Failed to enable ${pluginName}:`, err);
                     setFieldError('general', `Failed to enable ${pluginName}. Please try again.`);
                     } finally {
                     setSubmitting(false);
                     }
                 }}
 >
                 {({ isSubmitting }) => (
                 <Form>
                     <Box p={2}>
                     <Typography sx={{ mb: 2, fontFamily: 'Gilroy-Regular' }}>Click on the button below to Enable Custom API Catalogue</Typography>
                      <Divider sx={{ mt: 0 }} />
                      {/* Button container */}
                      <Box sx={{ paddingTop: 2.5, textAlign: "right" }}>
                       <Button
                      type="submit"
                      sx={{ backgroundColor: "black", color: "white", fontFamily: "Gilroy-Bold", fontSize: "14px", borderRadius: "8px", textTransform: "none", px: 4, transition: 'background-color 0.3s ease', '&:hover': { backgroundColor: "dimgray" }, '&:disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)', cursor: 'not-allowed' }, }}
                      variant="contained" size="small"
                      disabled={isSubmitting}
                     >
                    {isSubmitting ? 'Enabling...' : 'Enable'}
                     </Button>
                     </Box>
                 </Box>
 </Form>
                 )}
 </Formik>
                                ),
    };


    // --- Filter & Group Logic (remains the same) ---
    const filteredPlugins = pluginsData.filter((plugin) =>
        (selectedCategory === "All" || plugin.category === selectedCategory) &&
        plugin.name.toLowerCase().includes(search.toLowerCase())
    );
    const groupedPlugins = filteredPlugins.reduce((acc, plugin) => {
        if (!acc[plugin.category]) acc[plugin.category] = [];
        acc[plugin.category].push(plugin);
        return acc;
    }, {} as Record<string, typeof pluginsData>);

    // --- Modal Control Handlers (remains the same) ---
    const handleOpenModal = (pluginName: string) => {
        setSelectedPlugin(pluginName);
        setOpenModal(true);
    };
    const handleCloseModal = () => {
        setOpenModal(false);
        setSelectedPlugin(null);
    };

    // --- Snackbar Close Handler REMOVED ---

    // --- Dynamically get the content component based on selectedPlugin (remains the same) ---
    const ModalContent = selectedPlugin && installHandlers[selectedPlugin as keyof typeof installHandlers]
        ? installHandlers[selectedPlugin as keyof typeof installHandlers]
        : () => <Box p={2}><Typography>Loading or invalid plugin...</Typography></Box>;

    const currentPluginData = selectedPlugin ? pluginsData.find(plugin => plugin.name === selectedPlugin) : null;
    const currentPluginImage = currentPluginData?.image;

    // --- Render ---
    return (
        // No Fragment needed here anymore
        <Container
            disableGutters
            maxWidth={false}
            sx={{ padding: "24px", bgcolor: "#fafafa", minHeight: "100vh" }}>
                <MiniBot/>
            <Box >
                {/* Header Section (Styles unchanged) */}
                 <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    {/* Left Section */}
                    <Box>
                        <Breadcrumbs aria-label="breadcrumb" sx={{  fontSize: "0.875rem", color: "gray", mb: 2 }}>
                            <Link underline="hover" color="inherit" href="#" onClick={(event) => { event.preventDefault(); navigate("/integrations"); }}> Integrations </Link>
                            <Typography color="text.primary"> Explore Plugins </Typography>
                        </Breadcrumbs>
                        <Box display="flex" alignItems="center" >
                            <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate("/integrations")} sx={{ mr: 1, p: 0.5 }}> <ArrowBackIosNewIcon fontSize="small" /> </IconButton>
                            <Typography fontFamily="Gilroy-Bold" fontSize="18px"> Explore Plugins </Typography>
                        </Box>
                    </Box>
                    {/* Right Section (Search Bar) */}
                     <Box>
                        <TextField variant="filled" placeholder="Search by plugin name..." value={search} onChange={(e) => setSearch(e.target.value)} size="small" hiddenLabel sx={{
                        // Prefer using sx for styling consistency
                        backgroundColor: "#F5F5F5",
                        borderRadius: "8px",
                        width: "300px", // You can adjust width as needed
                        '& .MuiFilledInput-root': { // Target the input's root container
                            backgroundColor: "#f4f4f4",
                            borderRadius: "8px", // Ensure root also has border radius
                            minHeight: "45px",
                            overflow: 'hidden', // Helps contain background and border radius
                            '&:hover': {
                                backgroundColor: "#eeeeee", // Optional: Adjust hover color
                            },
                            '&.Mui-focused': {
                                backgroundColor: "#f4f4f4", // Keep background same on focus
                            },
                            // Remove the default FilledInput underline
                            '&::before, &::after': {
                                 borderBottom: 'none !important',
                            },
                        },
                         // Target the input element itself for font and padding
                         '& .MuiFilledInput-input': {
                            fontFamily: "Gilroy-Regular",
                            fontSize: "14px",
                            paddingTop: '12px', // Adjust padding for vertical centering within 45px height
                            paddingBottom: '12px',
                            paddingLeft: '0px', // Padding left handled by adornment
                            paddingRight: '12px',
                         }
                    }} InputProps={{ startAdornment: ( <InputAdornment position="start" sx={{ pl: '12px', pr: '8px', mt: '0 !important' }}> <img src="/assets/search-normal.svg" alt="Search" style={{ width: "16px", display: 'block' }} /> </InputAdornment> ), disableUnderline: true, }}/>
                    </Box>
                </Box>

                {/* Category Tabs (Styles unchanged) */}
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 3 }}>
                    {categories.map((category) => (
                        <Button key={category} onClick={() => setSelectedCategory(category)} variant="outlined" sx={{ borderRadius: "20px", textTransform: "none", fontWeight: 500, fontSize: "12px", padding: "4px 12px", borderWidth: "1px", backgroundColor: category === selectedCategory ? "#F5F5F5" : "transparent", color: "#333", borderColor: category === selectedCategory ? "#000" : "#DADADA", "&:hover": { backgroundColor: "#EAEAEA", borderColor: "#000" }, }}>
                            {category}
                        </Button>
                    ))}
                </Box>

                {/* Plugin Sections (Styles unchanged) */}
                {Object.entries(groupedPlugins).map(([category, plugins]) => (
                    <Box key={category} sx={{ mb: 4 }}>
                        <Typography fontSize="18px" fontWeight="bold" sx={{ mb: 2 }}>{category}</Typography>
                        <Typography fontSize="14px" fontFamily="Gilroy-regular" sx={{ mb: 2 }}>{sectionHeadings[category] || category}</Typography>
                        <Grid container spacing={2}>
                            {plugins.map((plugin) => {
                                const isInstalled = !!installationStatuses[plugin.name];
                                return(
                                    <Grid item xs={12} sm={6} md={4} key={plugin.id} mb={5}>
                                        <Paper sx={{ padding: 2, borderRadius: "12px", boxShadow: "none", border: "1px solid #E0E0E0", height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                            {/* Plugin Info */}
                                            <Box>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                                    <img src={plugin.image} alt={plugin.name} style={{ width: "40px", height: "40px" }} />
                                                    <Typography fontSize="16px" fontFamily="Gilroy-Bold">{plugin.name}</Typography>
                                                </Box>
                                                <Typography fontSize="14px" fontFamily="Gilroy-regular" sx={{ mt: 1, mb: 2 }}> {plugin.description} </Typography>
                                            </Box>
                                            {/* Install Button */}
                                            {plugin.name === "Grafana" && (
    <>
        {/* Check for the "Installation in progress" state first */}
        {isInstalled && !isGrafanaFaraday && !isLoadingStatuses ? (
            // Render your custom "Installation in progress" design here
            // This is NOT a button, so it won't have button behaviors or disabled style clashes
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between', // Adjust as needed if icon is part of text or separate
                padding: '6px 12px', // Adjust padding to match your image
                backgroundColor: '#FDB0220F', // Light orange background (approximated from image)
                borderRadius: '8px',
                color: '#000000', // Dark orange text color (approximated)
                fontFamily: 'Gilroy-Medium', // Assuming same font
                fontSize: '14px', // Adjust as needed
                alignSelf: 'flex-start',
                cursor: 'default', // To show it's not interactive
            }}>
                Installation in progress.
                {/* Replace with your actual icon. 
                  If it's an SVG image or a component:
                  <img src="/path-to-your-orange-spinner-icon.svg" alt="in progress" style={{ width: "16px", height: "16px", marginLeft: "8px" }} /> 
                  or <YourInProgressIconComponent />
                */}
               <img src="/assets/loader-2-fill.svg" alt="in progress" style={{ width: "16px", height: "16px", marginLeft: "8px",  padding: '6px 12px' }} />
            </div>
        ) : (
            // Render the MUI Button for all other states
            <Button
                variant="outlined"
                startIcon={!isInstalled && !isLoadingStatuses ? <img src="/assets/install-line.svg" alt="install" style={{ width: "16px", filter: "brightness(0)" }} /> : undefined}
                sx={{
                    alignSelf: 'flex-start',
                    borderRadius: "8px",
                    textTransform: "none",
                    color: "#333", // Default text color for enabled button
                    borderColor: "#DADADA", // Default border color
                    fontFamily: 'Gilroy-Medium',
                    "&:hover": {
                        backgroundColor: "#F5F5F5",
                        borderColor: "#000"
                    },
                    '&.Mui-disabled': { // Styles for when the MUI button itself is disabled
                        backgroundColor: 'rgba(0, 0, 0, 0.05)',
                        borderColor: 'rgba(0, 0, 0, 0.1)',
                        color: 'rgba(0, 0, 0, 0.4)',
                        cursor: 'default',
                        '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.05)',
                            borderColor: 'rgba(0, 0, 0, 0.1)'
                        }
                    },
                }}
                onClick={() => !isInstalled && handleOpenModal(plugin.name)}
                // The button is disabled if loading, or if it's installed AND is the special GrafanaFaraday case.
                // The "Installation in progress" state is now handled by the div above, so the button won't be rendered then.
                disabled={isViewer || isLoadingStatuses || (isInstalled && isGrafanaFaraday)}
            >
                {isLoadingStatuses ? "Loading..." : (isInstalled && isGrafanaFaraday ? "Installed" : "Install")}
            </Button>
        )}
    </>
)}
                                            {/* {plugin.name=="Grafana" && (
                                                 <Button
                                                 variant="outlined"
                                                 startIcon={!isInstalled ? <img src="/assets/install-line.svg" alt="install" style={{ width: "16px", filter: "brightness(0)" }} /> : undefined}
                                                 sx={{ alignSelf: 'flex-start', borderRadius: "8px", textTransform: "none", color: "#333", borderColor: "#DADADA", fontFamily: 'Gilroy-Medium', "&:hover": { backgroundColor: "#F5F5F5", borderColor: "#000" }, '&.Mui-disabled': { backgroundColor: 'rgba(0, 0, 0, 0.05)', borderColor: 'rgba(0, 0, 0, 0.1)', color: 'rgba(0, 0, 0, 0.4)', cursor: 'default', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.05)', borderColor: 'rgba(0, 0, 0, 0.1)' } }, }}
                                                 onClick={() => !isInstalled && handleOpenModal(plugin.name)}
                                                 disabled={isInstalled || isLoadingStatuses || isGrafanaFaraday}
                                             >
                                                 {isLoadingStatuses ? "Loading..." : (isInstalled ? (isGrafanaFaraday ? "Installed" : "Enabled(Installation in Progress)"): "Install")}
                                             </Button>
                                            )} */}
                                            {plugin.name!="Grafana" &&(
                                                <Button
                                                variant="outlined"
                                                startIcon={!isInstalled ? <img src="/assets/install-line.svg" alt="install" style={{ width: "16px", filter: "brightness(0)" }} /> : undefined}
                                                sx={{ alignSelf: 'flex-start', borderRadius: "8px", textTransform: "none", color: "#333", borderColor: "#DADADA", fontFamily: 'Gilroy-Medium', "&:hover": { backgroundColor: "#F5F5F5", borderColor: "#000" }, '&.Mui-disabled': { backgroundColor: 'rgba(0, 0, 0, 0.05)', borderColor: 'rgba(0, 0, 0, 0.1)', color: 'rgba(0, 0, 0, 0.4)', cursor: 'default', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.05)', borderColor: 'rgba(0, 0, 0, 0.1)' } }, }}
                                                onClick={() => !isInstalled && handleOpenModal(plugin.name)}
                                                disabled={isViewer || isInstalled || isLoadingStatuses}
                                            >
                                                {isLoadingStatuses ? "Loading..." : (isInstalled  ? "Installed" : "Install")}
                                            </Button>
                                            )}

                                            
                                            
                                        </Paper>
                                    </Grid>
                                )
                            })}
                        </Grid>
                    </Box>
                ))}

                {/* No Plugins Found Message (Styles unchanged) */}
                {filteredPlugins.length === 0 && !isLoadingStatuses && (
                    <Typography fontSize="16px" fontFamily="Gilroy-Bold" sx={{ mt: 2, textAlign: "center" }}> No plugins found matching your criteria. </Typography>
                )}
                {isLoadingStatuses && ( <Typography sx={{ mt: 2, textAlign: "center" }}>Loading plugin statuses...</Typography> )}
            </Box>

            {/* Single Modal Instance (Styles unchanged) */}
            <Modal
                open={openModal} onClose={handleCloseModal} aria-labelledby="plugin-modal-title" closeAfterTransition
                slotProps={{ backdrop: { sx: { backdropFilter: 'blur(1px)', backgroundColor: 'rgba(0, 0, 0, 0.1)' } } }}
            >
                <Fade in={openModal} timeout={{ enter: 400, exit: 400 }}>
                    <Box sx={modalStyle}>
                        {/* Modal Header */}
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0, px:2, py:1, }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                {currentPluginImage && ( <img src={currentPluginImage} alt={selectedPlugin ? `${selectedPlugin} Logo` : 'Plugin Logo'} style={{ width: '28px', height: '28px', display: 'block' }} /> )}
                                <Typography id="plugin-modal-title" fontFamily="Gilroy-SemiBold" fontSize="20px"> Install {selectedPlugin || "Plugin"} </Typography>
                            </Box>
                            <IconButton aria-label="close" onClick={handleCloseModal} sx={{ mr: 1 }} > <CloseIcon fontSize="small" /> </IconButton>
                        </Box>
                        <Divider sx={{ flexShrink: 0 }}/>
                        {/* Modal Content Area */}
                        <Box sx={{ overflowY: 'auto', flexGrow: 1, }}>
                            <ModalContent />
                        </Box>
                    </Box>
                </Fade>
            </Modal>

            {/* Snackbar Component REMOVED from here */}

        </Container>
    );
};

// export default ExplorePlugins;

const ExplorePlugins: React.FC = () => {
    const theme = useTheme();

    const getInitialDrawerState = () => {
        if (typeof window !== 'undefined') {
            const storedState = localStorage.getItem('isFaradayDrawerOpen');
            return storedState !== null ? JSON.parse(storedState) : true;
        }
        return true;
    };
    const [isDrawerActuallyOpen, setIsDrawerActuallyOpen] = useState<boolean>(getInitialDrawerState);

    const handleDrawerStateChange = (isOpenFromDrawer: boolean) => {
        setIsDrawerActuallyOpen(isOpenFromDrawer);
    };

    // Using your remembered calculation for marginLeft
    const currentMarginLeft = isDrawerActuallyOpen ? openDrawerWidth - 248 : collapsedDrawerWidth - 70;

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <MainDrawer
                // Assuming "integrations" is the correct index for Kyverno page
                // If Kyverno has its own top-level menu item, update this index.
                index={"integrations"} // Or a more specific index like "integrations-kyverno" if applicable
                onStateChange={handleDrawerStateChange}
            />
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    // bgcolor: '#fafafa', // bgcolor commented out as per your previous change
                    p: 0,
                    transition: theme.transitions.create('margin', {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.enteringScreen,
                    }),
                    // Using your remembered unit and calculation for marginLeft
                    marginLeft: `${currentMarginLeft}vh`,
                }}
            >
                <ExplorePluginsContent />
            </Box>
        </Box>
    );
};

export default ExplorePlugins;

// import React, { useState, useEffect} from "react";
// import {
//     Box,
//     Breadcrumbs,
//     Button,
//     Container,
//     Fade,
//     Grid,
//     Link,
//     Typography,
//     TextField,
//     InputAdornment,
//     Modal,
//     Divider,
//     IconButton,
//     Paper,
//     // Uncomment if you use theme for transitions/styles
//     // useTheme
// } from "@mui/material";
// // SearchIcon seems unused, remove if not needed
// // import SearchIcon from "@mui/icons-material/Search";
// import { Formik, Form, ErrorMessage } from "formik";
// import * as Yup from "yup";
// // Correct import for the Close icon
// import CloseIcon from "@mui/icons-material/Close";
// import { useNavigate } from 'react-router-dom';
// import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
// import queryGraphql from "../../helpers/ApiInterface";

// // Plugin categories (remains the same)
// const categories = [
//     "All",
//     "CI/CD Tools",
//     "Cost Agents",
//     "Git Providers",
//     "Kubernetes Stack",
//     "Observability",
//     "Security Policies",
//     // "Policies",
//     // "Cloud Providers",
//     // "IaC",
//     // "Incident Management",
//     // "Project Management",
//     // "Code Quality & Security",
//     // "Feature Management",
//     // "APM & Alerting",
// ];

// // Sample plugin data (remains the same)
// const pluginsData = [
//     { id: 1, name: "ArgoCD", category: "CI/CD Tools", description: "Lorem ipsum dolor sum it. Lorem ipsum dolor sum it.", image: "/assets/ArgoCD.svg" },
//     { id: 2, name: "Jenkins", category: "CI/CD Tools", description: "Lorem ipsum dolor sum it. Lorem ipsum dolor sum it.", image: "/assets/Jenkins.svg" },
//     { id: 3, name: "GCP Cost Agent", category: "Cost Agents", description: "Lorem ipsum dolor sum it. Lorem ipsum dolor sum it.", image: "/assets/gcp.svg" },
//     { id: 4, name: "AWS Cost Agent", category: "Cost Agents", description: "Lorem ipsum dolor sum it. Lorem ipsum dolor sum it.", image: "/assets/aws.svg" },
//     { id: 5, name: "Github", category: "Git Providers", description: "Lorem ipsum dolor sum it. Lorem ipsum dolor sum it.", image: "/assets/GitHub.svg" },
//     { id: 6, name: "Gitlab", category: "Git Providers", description: "Lorem ipsum dolor sum it. Lorem ipsum dolor sum it.", image: "/assets/Gitlab.svg" },
//     { id: 7, name: "Bitbucket", category: "Git Providers", description: "Lorem ipsum dolor sum it. Lorem ipsum dolor sum it.", image: "/assets/bit-bucket.svg" },
//     { id: 8, name: "Istio", category: "Kubernetes Stack", description: "Lorem ipsum dolor sum it. Lorem ipsum dolor sum it.", image: "/assets/istio.svg" },
//     { id: 9, name: "Kubernetes", category: "Kubernetes Stack", description: "Lorem ipsum dolor sum it. Lorem ipsum dolor sum it.", image: "/assets/kubernetes.svg" },
//     { id: 10, name: "Grafana", category: "Observability", description: "Lorem ipsum dolor sum it. Lorem ipsum dolor sum it.", image: "/assets/Grafana.svg" },
//     { id: 11, name: "Prometheus", category: "Observability", description: "Lorem ipsum dolor sum it. Lorem ipsum dolor sum it.", image: "/assets/Prometheus.svg" },
//     { id: 12, name: "CheckOV", category: "Security Policies", description: "Lorem ipsum dolor sum it. Lorem ipsum dolor sum it.", image: "/assets/checkov.svg" },
//     { id: 13, name: "Kyverno", category: "Security Policies", description: "Lorem ipsum dolor sum it. Lorem ipsum dolor sum it.", image: "/assets/kyverno.svg" },    
// ];

// // Section Headings (remains the same)
// const sectionHeadings: Record<string, string> = {
//     "Cost Agents": "Cost Optimization Tools",
//     "CI/CD Tools": "CI/CD Automation",
//     "Git Providers": "Version Control Integrations",
//     "Kubernetes Stack": "Container Orchestration Solutions",
//     "Policies": "Security and Compliance Policies",
    
//     // Add more as needed
// };


// const apiKeyToPluginName: Record<string, string> = {
//     costAgent: "GCP Cost Agent", // *** ASSUMPTION: API 'costAgent' maps to 'GCP Cost Agent' ***
//     kyverno: "Kyverno",
//     checkov: "CheckOV", // Note: Plugin data name is "CheckOV", ensure case matches if needed anywhere else
//     prometheus: "Prometheus",
//     // Add mappings for other integrations if fetched by the API, e.g., github: "Github"
// };

// // --- Style for the main modal Box ---
// // Defined outside the component for clarity
// const modalStyle = {
//     position: "absolute" as "absolute",
//     top: "50%",
//     left: "50%",
//     transform: "translate(-50%, -50%)",
//     width: 700, // Adjust as needed
//     maxWidth: '90vw',
//     maxHeight: '90vh',
//     bgcolor: "white",
//     border: "0px",
//     boxShadow: 24,
//     borderRadius: "16px",
//     outline: 'none',
//     display: 'flex',
//     flexDirection: 'column', // Stack header, content, footer vertically
// };


// // --- Main Component ---
// const ExplorePlugins: React.FC = () => {
//     const navigate = useNavigate();
//     // --- State ---
//     const [installationStatuses, setInstallationStatuses] = useState<Record<string, boolean>>({});
//     const [isLoadingStatuses, setIsLoadingStatuses] = useState<boolean>(true); // Start true

//     const [search, setSearch] = useState("");
//     const [selectedCategory, setSelectedCategory] = useState("All");
    
//     // --- State for Modal ---
//     const [openModal, setOpenModal] = useState(false);
//     const [selectedPlugin, setSelectedPlugin] = useState<string | null>(null);
//     // Uncomment to use theme:
//     // const theme = useTheme();
//     const fetchInstallationStatuses = async (): Promise<Record<string, boolean>> => {
//         console.log("Fetching installation statuses from API...");
//         const graphqlQuery = ` query Query { getIntegrations { costAgent { enabled } kyverno { enabled } checkov { enabled } prometheus { enabled } id } } `;
//         try {
//             const response = await queryGraphql(graphqlQuery);
//             // *** CHANGE 2 (LOG 1): Added log for raw API response ***
//             console.log("LOG 1: Raw API Response:", JSON.stringify(response, null, 2));

//             const integrationsData = response?.getIntegrations;
//             const statuses: Record<string, boolean> = {};

//             if (integrationsData) {
//                 // *** CHANGE 2 (LOG 2): Added log for parsed data object ***
//                 console.log("LOG 2: Parsed integrationsData:", integrationsData);
//                 for (const apiKey in apiKeyToPluginName) {
//                     const pluginName = apiKeyToPluginName[apiKey];
//                     // Check if the key from YOUR map exists in the API data AND if its 'enabled' prop is strictly true
//                     const isEnabledInApi = integrationsData[apiKey]?.enabled === true;
//                     statuses[pluginName] = isEnabledInApi;
//                     // *** CHANGE 2 (LOG 3): Added log for mapping details ***
//                     console.log(`LOG 3: Checking API key: '${apiKey}' -> Plugin Name: '${pluginName}'. API value:`, integrationsData[apiKey], ` API enabled: ${integrationsData[apiKey]?.enabled}, Setting status: ${isEnabledInApi}`);
//                 }
//             } else {
//                  console.warn("No integration data found in API response (response?.data?.getIntegrations was null/undefined). Defaulting mapped plugins to false.");
//                  // Default mapped plugins to FALSE
//                  for (const apiKey in apiKeyToPluginName) {
//                      // *** CHANGE 3: Fixed default value to false in else block ***
//                      statuses[apiKeyToPluginName[apiKey]] = false;
//                  }
//             }
//             // *** CHANGE 2 (LOG 4): Added log for final status object ***
//             console.log("LOG 4: Final statuses object to be set:", statuses);
//             return statuses;
//         } catch (error) {
//             console.error("Error fetching or processing integration statuses:", error);
//              // *** CHANGE 3: Default to false on error ***
//              const statuses: Record<string, boolean> = {};
//              for (const apiKey in apiKeyToPluginName) { statuses[apiKeyToPluginName[apiKey]] = false; }
//             return statuses; // Return default false statuses on catch
//         }
//     };

//     // --- useEffect to Fetch Statuses on Mount ---
//     // This fetches data ONCE when the component loads/mounts
//     useEffect(() => {
//         let isMounted = true;
//         console.log("useEffect running to fetch statuses..."); // Added log
//         setIsLoadingStatuses(true); // Indicate loading starts
//         fetchInstallationStatuses()
//             .then(statuses => {
//                 if (isMounted) {
//                     // *** CHANGE 2 (LOG 5): Added log before setting state ***
//                     console.log("LOG 5: Setting installationStatuses state with:", statuses);
//                     setInstallationStatuses(statuses); // Update state with fetched data
//                 }
//             })
//             .catch(error => {
//                 console.error("Error setting installation statuses:", error);
//                 // Optionally set a global error state to show feedback to the user
//             })
//             .finally(() => { // *** CHANGE 4: Using finally for cleaner loading state update ***
//                  // Ensure loading indicator is turned off regardless of success/error
//                  if (isMounted) {
//                     setIsLoadingStatuses(false);
//                  }
//             });

//         // Cleanup function to prevent state updates if component unmounts during fetch
//         return () => { isMounted = false; };
//     }, []);
//     // const fetchInstallationStatuses = async (): Promise<Record<string, boolean>> => {
//     //     console.log("Fetching installation statuses from API...");
//     //     const graphqlQuery = `
//     //         query Query {
//     //           getIntegrations {
//     //             costAgent {
//     //               enabled
//     //             }
//     //             kyverno {
//     //               enabled
//     //             }
//     //             checkov {
//     //               enabled
//     //             }
//     //             prometheus {
//     //               enabled
//     //             }
//     //             id
//     //           }
//     //         }
//     //     `;

//     //     try {
//     //         const response = await queryGraphql(graphqlQuery);
//     //         const integrationsData = response?.data?.getIntegrations;
//     //         const statuses: Record<string, boolean> = {};

//     //         if (integrationsData) {
//     //             console.log("Received integration data:", integrationsData);
//     //             for (const apiKey in apiKeyToPluginName) {
//     //                 const pluginName = apiKeyToPluginName[apiKey];
//     //                 if (integrationsData[apiKey] && typeof integrationsData[apiKey].enabled === 'boolean') {
//     //                     statuses[pluginName] = integrationsData[apiKey].enabled;
//     //                 } else {
//     //                     statuses[pluginName] =false; // Default to false if not found or enabled is not boolean
//     //                 }
//     //             }
//     //         } else {
//     //             console.warn("No integration data found in API response:", response);
//     //              // Default all known mappable plugins to false if API data is missing
//     //              for (const apiKey in apiKeyToPluginName) {
//     //                  statuses[apiKeyToPluginName[apiKey]] = false;
//     //              }
//     //         }
//     //         console.log("Processed statuses:", statuses);
//     //         return statuses;
//     //     } catch (error) {
//     //         console.error("Error fetching or processing integration statuses:", error);
//     //         return {}; // Return empty on error
//     //     }
//     // };
//     // useEffect(() => {
//     //     let isMounted = true;
//     //     setIsLoadingStatuses(true);
//     //     fetchInstallationStatuses()
//     //         .then(statuses => {
//     //             if (isMounted) {
//     //                 setInstallationStatuses(statuses);
//     //                 setIsLoadingStatuses(false);
//     //                 console.log("Installation statuses state updated:", statuses);
//     //             }
//     //         })
//     //         .catch(error => {
//     //             console.error("Error setting installation statuses:", error);
//     //             if (isMounted) {
//     //                 setIsLoadingStatuses(false);
//     //             }
//     //         });
//     //     return () => { isMounted = false; }; // Cleanup
//     // }, []); // Run once on mount

//     // --- Refactored installHandlers ---
// // They now return only the specific content JSX for the modal body
// const installHandlers = {

    
//     "GCP Cost Agent": () => (
        
//         // Return only the Formik component and its inner elements
//         // The Modal Frame (title, close button) is handled outside this handler
//         <Formik
//             initialValues={{projectId: "", datasetId: "", tableId: "" }}
//             validationSchema={Yup.object({
//                 projectId: Yup.string().required("Project ID is required").matches( /^[a-zA-Z0-9_-]+$/,"Project ID can only contain letters, numbers, underscores (_), and hyphens (-)"),
//                 datasetId: Yup.string().required("Dataset ID is required").matches( /^[a-zA-Z0-9_-]+$/,"Dataset ID can only contain letters, numbers, underscores (_), and hyphens (-)"),
//                 tableId: Yup.string().required("Table ID is required").matches( /^[a-zA-Z0-9_-]+$/,"Table ID can only contain letters, numbers, underscores (_), and hyphens (-)"),
//             })}
//             onSubmit={async (values, { setSubmitting }) => {
//                 console.log("GCP Cost Agent Submitted Data:", values);
//                 setSubmitting(true); // Set submitting state
//                 try {
//                     const data = await queryGraphql(
//                         `
//                             mutation Mutation($input: UpdateIntegrationsInput) {
//                                 updateIntegration(input: $input) {
//                                     costAgent {
//                                         enabled
//                                     }
//                                 }
//                             }
//                         `,
//                         {
//                             "input": {
//                                 costAgent: {
//                                     datasetId: values.datasetId,
//                                     enabled: true,
//                                     projectId: values.projectId,
//                                     tableId: values.tableId,
//                                 }
//                             }
//                         }
//                     );

//                     // Check if mutation was successful (adjust condition as needed)
//                     if (data?.updateIntegration?.costAgent?.enabled) {
//                         console.log("GCP Cost Agent installation successful via API.");
//                         // *** Update local state immediately ***
//                         setInstallationStatuses(prevStatuses => ({
//                             ...prevStatuses,
//                             ["GCP Cost Agent"]: true // Update status for this plugin
//                         }));
//                         console.log("GCP Cost Agent status updated locally to true.");
//                         handleCloseModal(); // Close modal on success
//                     } else {
//                         console.error("GCP Cost Agent installation API call did not confirm success:", data);
//                         // TODO: Show error message to user in modal?
//                     }
//                 } catch (error) {
//                     console.error("Error submitting GCP Cost Agent form:", error);
//                     // TODO: Show error message to user in modal?
//                 } finally {
//                     setSubmitting(false); // Reset submitting state
//                 }
//                 handleCloseModal()
//             }}
//         >
//             {({ handleChange, handleBlur, values, errors, touched }) => (
//                 <Form> {/* Use Formik's Form component */}

//                 {/* Logo Placeholder (optional, kept commented) */}
//                 {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, pt: 1.5, mb: 1 }}>
//                     <img src="/assets/gcp.svg" alt="Plugin Logo" style={{ width: '28px', height: '28px' }} />
//                 </Box> */}
   
//                {/* Service Account Display and Instructions (unchanged) */}
//                <Box px={2.5} pt={1} pb={2}>
//                    <Typography fontFamily="Gilroy-Regular" fontSize="14px" color="gray" gutterBottom>
//                        Grant Permissions to Service Account
//                    </Typography>
//                    <Box sx={{ p: 1, bgcolor: "#F5F5F5", borderRadius: "8px", fontFamily: "monospace", fontSize: "14px", wordBreak: "break-all", border: '1px solid #E0E0E0' }}> 
                   
//                    <Typography component="span" sx={{ fontFamily: 'monospace', fontSize: '14px', color: '#333' }}>
//                            gcp-cost-agent@your-project.iam.gserviceaccount.com
//                        </Typography> 
//                        </Box>
//                    {/* <Box sx={{ p: 1, bgcolor: "#F5F5F5", borderRadius: "8px", fontFamily: "monospace", fontSize: "14px", wordBreak: "break-all", border: '1px solid #E0E0E0' }}> */}
//                    {/* <Grid container spacing={2.5}>
//                    <Grid item xs={12} md={4}> 
//                    <TextField
//                                fullWidth
//                                variant="outlined"
//                                name="serviceAccount"
//                                label="Service Account"
//                                value={values.serviceAccount}
//                                onChange={handleChange}
//                                onBlur={handleBlur}
//                                error={!!errors.serviceAccount && touched.serviceAccount}
//                                helperText={touched.serviceAccount ? errors.serviceAccount : ""}
                               
//                                 sx={{
//                                     '& .MuiInputBase-input': { fontFamily: 'Gilroy-Regular' },
//                                     '& label': { fontFamily: 'Gilroy-Regular' },
//                                     '& .MuiFormHelperText-root': { fontFamily: 'Gilroy-Regular' },
//                                     '& .MuiOutlinedInput-root': {
//                                         borderRadius: '8px',
//                                         backgroundColor: '#F5F5F5', // --- ADDED ---
//                                         transition: 'border-color 200ms ease-in-out, background-color 200ms ease-in-out', // Smooth
//                                         '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' }, // --- Hide default border ---
//                                         '&:hover':{ backgroundColor: '#EEEEEE', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' } },
//                                         '&.Mui-focused': {
//                                             backgroundColor: '#F5F5F5',
//                                             '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent', borderWidth: '1px' }
//                                         },
//                                          '& legend': { display: 'none' },
//                                     },
//                                     '& label.Mui-focused': { color: 'rgba(0, 0, 0, 0.6)' }
//                                 }}
//                            />
                        
//                        {/* </Grid>
//                        </Grid> */} 
//                    {/* </Box> */}
//                </Box>
   
//                 {/* Input Fields using Outlined Variant with Background */}
//                <Box px={2.5} pb={2}>
//                    <Grid container spacing={2.5}>
//                        {/* Project ID */}
//                        <Grid item xs={12} md={4}>
//                        <TextField
//                                fullWidth
//                                variant="outlined"
//                                name="projectId"
//                                label="Project ID"
//                                value={values.projectId}
//                                onChange={handleChange}
//                                onBlur={handleBlur}
//                                error={!!errors.projectId && touched.projectId}
//                                helperText={touched.projectId ? errors.projectId : ""}
                               
//                                 sx={{
//                                     '& .MuiInputBase-input': { fontFamily: 'Gilroy-Regular' },
//                                     '& label': { fontFamily: 'Gilroy-Regular' },
//                                     '& .MuiFormHelperText-root': { fontFamily: 'Gilroy-Regular' },
//                                     '& .MuiOutlinedInput-root': {
//                                         borderRadius: '8px',
//                                         backgroundColor: '#F5F5F5', // --- ADDED ---
//                                         transition: 'border-color 200ms ease-in-out, background-color 200ms ease-in-out', // Smooth
//                                         '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' }, // --- Hide default border ---
//                                         '&:hover':{ backgroundColor: '#EEEEEE', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' } },
//                                         '&.Mui-focused': {
//                                             backgroundColor: '#F5F5F5',
//                                             '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent', borderWidth: '1px' }
//                                         },
//                                          '& legend': { display: 'none' },
//                                     },
//                                     '& label.Mui-focused': { color: 'rgba(0, 0, 0, 0.6)' }
//                                 }}
//                            />
//                        </Grid>
//                        {/* Dataset ID */}
//                        <Grid item xs={12} md={4}>
//                            <TextField
//                                fullWidth
//                                variant="outlined"
//                                name="datasetId"
//                                label="Dataset ID"
//                                value={values.datasetId}
//                                onChange={handleChange}
//                                onBlur={handleBlur}
//                                error={!!errors.datasetId && touched.datasetId}
//                                helperText={touched.datasetId ? errors.datasetId : ""}
//                                 sx={{
//                                     '& .MuiInputBase-input': { fontFamily: 'Gilroy-Regular' },
//                                     '& label': { fontFamily: 'Gilroy-Regular' },
//                                     '& .MuiFormHelperText-root': { fontFamily: 'Gilroy-Regular' },
//                                     '& .MuiOutlinedInput-root': {
//                                         borderRadius: '8px',
//                                         backgroundColor: '#F5F5F5', // --- ADDED ---
//                                         transition: 'border-color 200ms ease-in-out, background-color 200ms ease-in-out', // Smooth
//                                         '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' }, // --- Hide default border ---
//                                         '&:hover':{ backgroundColor: '#EEEEEE', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' } },
//                                         '&.Mui-focused': {
//                                             backgroundColor: '#F5F5F5',
//                                             '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent', borderWidth: '1px' }
//                                         },
//                                          '& legend': { display: 'none' },
//                                     },
//                                     '& label.Mui-focused': { color: 'rgba(0, 0, 0, 0.6)' }
//                                 }}
//                            />
//                        </Grid>
//                        {/* Table ID */}
//                        <Grid item xs={12} md={4}>
//                            <TextField
//                                fullWidth
//                                variant="outlined"
//                                name="tableId"
//                                label="Table ID"
//                                value={values.tableId}
//                                onChange={handleChange}
//                                onBlur={handleBlur}
//                                error={!!errors.tableId && touched.tableId}
//                                helperText={touched.tableId ? errors.tableId : ""}
//                                 sx={{
//                                     '& .MuiInputBase-input': { fontFamily: 'Gilroy-Regular' },
//                                     '& label': { fontFamily: 'Gilroy-Regular' },
//                                     '& .MuiFormHelperText-root': { fontFamily: 'Gilroy-Regular' },
//                                     '& .MuiOutlinedInput-root': {
//                                         borderRadius: '8px',
//                                         backgroundColor: '#F5F5F5', // --- ADDED ---
//                                         transition: 'border-color 200ms ease-in-out, background-color 200ms ease-in-out', // Smooth
//                                         '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' }, // --- Hide default border ---
//                                         '&:hover':{ backgroundColor: '#EEEEEE', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' } },
//                                         '&.Mui-focused': {
//                                             backgroundColor: '#F5F5F5',
//                                             '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent', borderWidth: '1px' }
//                                         },
//                                         '& legend': { display: 'none' },
//                                     },
//                                     '& label.Mui-focused': { color: 'rgba(0, 0, 0, 0.6)' }
//                                 }}
//                            />
//                        </Grid>
//                    </Grid>
//                </Box>
   
//                <Divider sx={{ mt: 2 }} />
   
//                {/* Action Button (unchanged) */}
//                <Box sx={{ p: 2.5, textAlign: "right" }}>
//                    <Button
//                        sx={{
//                            backgroundColor: "black", color: "white",
//                            fontFamily: "Gilroy-Bold", fontSize: "14px", borderRadius: "8px",
//                            textTransform: "none", px: 4, position: 'relative',
//                            transition: 'background-color 0.3s ease',
//                            '&:hover': { backgroundColor: "dimgray" },
//                            '&:disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)', cursor: 'not-allowed' },
//                        }}
//                        variant="contained" size="small" type="submit"
//                        //disabled={isSubmitting}
//                    >
//                        Install
//                    </Button>
//                </Box>
//            </Form>
//             )}
//         </Formik>
//     ),
//     // Other handlers return simple Typography or specific components wrapped in a Box for padding
//     "CheckOV": () => (
//         <Formik
//             initialValues={{}} // No initial values needed for fields
//             // No validation schema needed unless you add fields later
//             onSubmit={async (values, { setSubmitting, setFieldError }) => {
//                 console.log("Enabling Checkov (Formik onSubmit)...");
//                 setSubmitting(true);
//                 //setFieldError('general', null); // Clear previous general errors

//                 const ENABLE_CHECKOV_MUTATION = `
//                     mutation Mutation($input: UpdateIntegrationsInput) {
//                       updateIntegration(input: $input) {
//                         checkov {
//                           enabled
//                         }
//                       }
//                     }
//                 `;

//                 try {
//                     const result = await queryGraphql(
//                         ENABLE_CHECKOV_MUTATION,
//                         {
//                             "input": {
//                                 checkov: {
//                                     enabled: true,
//                                 }
//                             }
//                         }
//                     );

//                     console.log("Checkov Enable Result:", result);

//                     if (result?.updateIntegration?.checkov?.enabled) {
//                         console.log("Checkov enabled successfully via API.");
//                         setInstallationStatuses(prevStatuses => ({
//                             ...prevStatuses,
//                             ["CheckOV"]: true
//                         }));
//                         console.log("CheckOV status updated locally to true.");
//                         handleCloseModal(); // Close modal on success
//                     } else {
//                         console.error("Checkov enabling API call did not confirm success:", result);
//                         setFieldError('general', "Failed to enable Checkov. API did not confirm success.");
//                     }
//                 } catch (err) {
//                     console.error("Failed to enable Checkov:", err);
//                     //setFieldError('general', `Failed to enable Checkov: ${err.message || 'Please try again.'}`);
//                 } finally {
//                     setSubmitting(false); // Reset submitting state via Formik
//                 }
//             }}
//         >
//             {({ isSubmitting, errors }) => ( // Get isSubmitting and errors from Formik
//                 <Form> {/* Use Formik's Form component */}

//                      {/* Display general errors if set */}
//                      {/* {errors.general && (
//                         <Box px={2.5} pt={1}>
//                             <Typography color="error" variant="body2" sx={{fontFamily: 'Gilroy-Regular'}}>
//                                 {errors.general}
//                             </Typography>
//                         </Box>
//                      )} */}

//                     {/* Main content area */}
//                     {/* Using p:2 padding like the previous version, adjust if needed */}
//                     <Box p={2}>
//                         <Typography sx={{ mb: 2, fontFamily: 'Gilroy-Regular' }}> {/* Added font */}
//                             Click on the button below to Enable Checkov
//                         </Typography>
//                         <Divider sx={{ mt: 0 }} />
//                         {/* Button container - aligning with GCP handler */}
//                         <Box sx={{ p: 2.5, textAlign: "right" }}>
//                             <Button
//                                 type="submit" // Button triggers Formik's onSubmit
//                                 sx={{
//                                     backgroundColor: "black", color: "white",
//                                     fontFamily: "Gilroy-Bold", fontSize: "14px", borderRadius: "8px",
//                                     textTransform: "none", px: 4, position: 'relative',
//                                     transition: 'background-color 0.3s ease',
//                                     '&:hover': { backgroundColor: "dimgray" },
//                                     '&:disabled': {
//                                         backgroundColor: 'rgba(0, 0, 0, 0.12)',
//                                         color: 'rgba(0, 0, 0, 0.26)',
//                                         cursor: 'not-allowed',
//                                     },
//                                 }}
//                                 variant="contained"
//                                 size="small"
//                                 disabled={isSubmitting} // Disable based on Formik's state
//                             >
//                                 {isSubmitting ? 'Enabling...' : 'Enable'}
//                             </Button>
//                         </Box>

//                         {/* Divider */}
                        
//                     </Box>
//                 </Form>
//             )}
//         </Formik>
//     ),
    
//     "Kyverno": () =>(
//         <Formik
//             initialValues={{}} // No initial values needed for fields
//             // No validation schema needed unless you add fields later
//             onSubmit={async (values, { setSubmitting, setFieldError }) => {
//                 console.log("Enabling Kyverno (Formik onSubmit)...");
//                 setSubmitting(true);
//                 //setFieldError('general', null); // Clear previous general errors

//                 const ENABLE_KYVERNO_MUTATION = `
//                     mutation Mutation($input: UpdateIntegrationsInput) {
//                       updateIntegration(input: $input) {
//                         kyverno {
//                           enabled
//                         }
//                       }
//                     }
//                 `;

//                 try {
//                     const result = await queryGraphql(
//                         ENABLE_KYVERNO_MUTATION,
//                         {
//                             "input": {
//                                 kyverno: {
//                                     enabled: true,
//                                 }
//                             }
//                         }
//                     );

//                     console.log("Kyverno Enable Result:", result);

//                     if (result?.updateIntegration?.kyverno?.enabled) {
//                         console.log("Kyverno enabled successfully via API.");
//                         setInstallationStatuses(prevStatuses => ({
//                             ...prevStatuses,
//                             ["Kyverno"]: true
//                         }));
//                         console.log("Kyverno status updated locally to true.");
//                         handleCloseModal(); // Close modal on success
//                     } else {
//                         console.error("Kyverno enabling API call did not confirm success:", result);
//                         setFieldError('general', "Failed to enable Kyverno. API did not confirm success.");
//                     }
//                 } catch (err) {
//                     console.error("Failed to enable Kyverno:", err);
//                     //setFieldError('general', `Failed to enable Checkov: ${err.message || 'Please try again.'}`);
//                 } finally {
//                     setSubmitting(false); // Reset submitting state via Formik
//                 }
//             }}
//         >
//             {({ isSubmitting, errors }) => ( // Get isSubmitting and errors from Formik
//                 <Form> {/* Use Formik's Form component */}

//                      {/* Display general errors if set */}
//                      {/* {errors.general && (
//                         <Box px={2.5} pt={1}>
//                             <Typography color="error" variant="body2" sx={{fontFamily: 'Gilroy-Regular'}}>
//                                 {errors.general}
//                             </Typography>
//                         </Box>
//                      )} */}

//                     {/* Main content area */}
//                     {/* Using p:2 padding like the previous version, adjust if needed */}
//                     <Box p={2}>
//                         <Typography sx={{ mb: 2, fontFamily: 'Gilroy-Regular' }}> {/* Added font */}
//                             Click on the button below to Enable Kyverno
//                         </Typography>
//                         <Divider sx={{ mt: 0 }} />
//                         {/* Button container - aligning with GCP handler */}
//                         <Box sx={{ p: 2.5, textAlign: "right" }}>
//                             <Button
//                                 type="submit" // Button triggers Formik's onSubmit
//                                 sx={{
//                                     backgroundColor: "black", color: "white",
//                                     fontFamily: "Gilroy-Bold", fontSize: "14px", borderRadius: "8px",
//                                     textTransform: "none", px: 4, position: 'relative',
//                                     transition: 'background-color 0.3s ease',
//                                     '&:hover': { backgroundColor: "dimgray" },
//                                     '&:disabled': {
//                                         backgroundColor: 'rgba(0, 0, 0, 0.12)',
//                                         color: 'rgba(0, 0, 0, 0.26)',
//                                         cursor: 'not-allowed',
//                                     },
//                                 }}
//                                 variant="contained"
//                                 size="small"
//                                 disabled={isSubmitting} // Disable based on Formik's state
//                             >
//                                 {isSubmitting ? 'Enabling...' : 'Enable'}
//                             </Button>
//                         </Box>

//                         {/* Divider */}
                        
//                     </Box>
//                 </Form>
//             )}
//         </Formik>
//     ),
    
   
//     "AWS Cost Agent": () => <Box p={2}><Typography>Coming Soon!!</Typography></Box>,
//     "Gitlab": () => <Box p={2}><Typography>Coming Soon!!</Typography></Box>,
//     "Github": () => <Box p={2}><Typography>Coming Soon!!</Typography></Box>,
//     "Bitbucket": () => <Box p={2}><Typography>Coming Soon!!</Typography></Box>,
//     "Istio": () => <Box p={2}><Typography>Coming Soon!!</Typography></Box>,
//     "Kubernetes": () => <Box p={2}><Typography>Coming Soon!!</Typography></Box>,
//     "Grafana": () => <Box p={2}><Typography>Coming Soon!!</Typography></Box>,
//     "Prometheus": () => <Box p={2}><Typography>Coming Soon!!</Typography></Box>,
//     "ArgoCD": () => <Box p={2}><Typography>Coming Soon!!</Typography></Box>,
//     "Jenkins": () => <Box p={2}><Typography>Coming Soon!!</Typography></Box>,
// };


//     // --- Filter & Group Logic --- (remains the same)
//     const filteredPlugins = pluginsData.filter((plugin) =>
//         (selectedCategory === "All" || plugin.category === selectedCategory) &&
//         plugin.name.toLowerCase().includes(search.toLowerCase())
//     );
//     const groupedPlugins = filteredPlugins.reduce((acc, plugin) => {
//         if (!acc[plugin.category]) acc[plugin.category] = [];
//         acc[plugin.category].push(plugin);
//         return acc;
//     }, {} as Record<string, typeof pluginsData>);

//     // --- Modal Control Handlers ---
//     const handleOpenModal = (pluginName: string) => {
//         setSelectedPlugin(pluginName);
//         setOpenModal(true);
//     };

//     const handleCloseModal = () => {
//         setOpenModal(false);
//         // It's good practice to reset the selected plugin when closing
//         // to avoid briefly showing old content if the modal re-opens quickly
//         setSelectedPlugin(null);
//     };

//     // --- Dynamically get the content component based on selectedPlugin ---
//     const ModalContent = selectedPlugin && installHandlers[selectedPlugin as keyof typeof installHandlers]
//         ? installHandlers[selectedPlugin as keyof typeof installHandlers]
//         : () => <Box p={2}><Typography>Loading or invalid plugin...</Typography></Box>; // Fallback

//     const currentPluginData = selectedPlugin
//         ? pluginsData.find(plugin => plugin.name === selectedPlugin)
//         : null; // Find the full data object for the selected plugin
    
//     const currentPluginImage = currentPluginData?.image; // Get the image path safely
    
//     // --- Render ---
//     return (
        
//         // Main container Box for the whole page/component

//         <Container 
//         disableGutters
// 			maxWidth={false}
// 			sx={{ padding: "24px", bgcolor: "#fafafa", minHeight: "100vh" }}>
//             <Box marginLeft="100px">
//                 {/* Header Section */}
//                 <Box
//             sx={{
//                 display: 'flex',            // Enable Flexbox layout
//                 justifyContent: 'space-between', // Push children to opposite ends
//                 alignItems: 'center',       // Align children vertically in the center
//                 mb: 3                       // Margin below the entire header row
//             }}
//         >
//             {/* Left Section (Header: Breadcrumbs + Back Button/Title) */}
//             {/* This Box groups the header elements together */}
//             <Box>
//                 {/* --- Breadcrumbs --- */}
//                 {/* Reduced margin-bottom as the spacing is now between this block and the Search Bar */}
//                 <Breadcrumbs aria-label="breadcrumb"  sx={{ ml: "-100px", fontSize: "0.875rem", color: "gray", mb: 2 }}>
//                     <Link
//                         underline="hover"
//                         color="inherit"
//                         href={"/integrations/integrations"}
//                         onClick={(event) => {
//                             event.preventDefault();
//                             navigate("/integrations/integrations");
//                         }}
//                     >
//                         Integrations
//                     </Link>
//                     <Typography color="text.primary">
//                         Explore Plugins
//                     </Typography>
//                 </Breadcrumbs>

//                 {/* --- Back Button and Title --- */}
//                 <Box display="flex" alignItems="center" marginLeft= "-100px">
//                     <IconButton
//                         edge="start"
//                         aria-label="back"
//                         color="inherit"
//                         onClick={() => navigate("/integrations/integrations")}
//                         sx={{ mr: 1, p: 0.5 }} // Adjusted padding/margin for potentially better alignment
//                     >
//                         <ArrowBackIosNewIcon fontSize="small" />
//                     </IconButton>
//                     <Typography fontFamily="Gilroy-Bold" fontSize="18px">
//                         Explore Plugins
//                     </Typography>
//                 </Box>
//             </Box> {/* End of Left Section */}

//             {/* Right Section (Search Bar) */}
//             {/* This Box groups the search bar (though it's just one element here) */}
//             <Box>
//                 <TextField
//                     variant="filled"
//                     placeholder="Search by plugin name..."
//                     value={search}
//                     onChange={(e) => setSearch(e.target.value)}
//                     size="small"
//                     hiddenLabel
                    // sx={{
                    //     // Prefer using sx for styling consistency
                    //     backgroundColor: "#F5F5F5",
                    //     borderRadius: "8px",
                    //     width: "300px", // You can adjust width as needed
                    //     '& .MuiFilledInput-root': { // Target the input's root container
                    //         backgroundColor: "#f4f4f4",
                    //         borderRadius: "8px", // Ensure root also has border radius
                    //         minHeight: "45px",
                    //         overflow: 'hidden', // Helps contain background and border radius
                    //         '&:hover': {
                    //             backgroundColor: "#eeeeee", // Optional: Adjust hover color
                    //         },
                    //         '&.Mui-focused': {
                    //             backgroundColor: "#f4f4f4", // Keep background same on focus
                    //         },
                    //         // Remove the default FilledInput underline
                    //         '&::before, &::after': {
                    //              borderBottom: 'none !important',
                    //         },
                    //     },
                    //      // Target the input element itself for font and padding
                    //      '& .MuiFilledInput-input': {
                    //         fontFamily: "Gilroy-Regular",
                    //         fontSize: "14px",
                    //         paddingTop: '12px', // Adjust padding for vertical centering within 45px height
                    //         paddingBottom: '12px',
                    //         paddingLeft: '0px', // Padding left handled by adornment
                    //         paddingRight: '12px',
                    //      }
                    // }}
//                     InputProps={{
//                         startAdornment: (
//                            <InputAdornment position="start" sx={{ pl: '12px', pr: '8px', mt: '0 !important' }}> {/* Adjust Adornment padding */}
//                                 <img src="/assets/search-normal.svg" alt="Search" style={{ width: "16px", display: 'block' }} /> {/* display:block helps vertical align */}
//                             </InputAdornment>
//                         ),
//                         disableUnderline: true, // Still good practice, though sx handles it visually
//                         // Removed inline style, using sx prop above is preferred
//                     }}
//                 />
//             </Box> {/* End of Right Section */}

//         </Box> 
//                 {/* Category Tabs */}
//                 <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 3 }}>
//                     {categories.map((category) => (
//                         <Button
//                             key={category}
//                             onClick={() => setSelectedCategory(category)}
//                             variant="outlined"
//                             sx={{ /* Your category button styles */
//                                 borderRadius: "20px", textTransform: "none", fontWeight: 500, fontSize: "12px",
//                                 padding: "4px 12px", borderWidth: "1px",
//                                 backgroundColor: category === selectedCategory ? "#F5F5F5" : "transparent",
//                                 color: "#333", borderColor: category === selectedCategory ? "#000" : "#DADADA",
//                                 "&:hover": { backgroundColor: "#EAEAEA", borderColor: "#000" },
//                              }}
//                         >
//                             {category}
//                         </Button>
//                     ))}
//                 </Box>
                
//                 {/* Plugin Sections */}
//                 {Object.entries(groupedPlugins).map(([category, plugins]) => (
//                     <Box key={category} sx={{ mb: 4 }}>
//                         {/* Section Heading */}
//                         <Typography fontSize="18px" fontWeight="bold" sx={{ mb: 2 }}>{category}</Typography>
//                         <Typography fontSize="14px" fontFamily="Gilroy-regular" sx={{ mb: 2 }}>
//                             {sectionHeadings[category] || category} {/* Fallback to category name if no heading */}
//                         </Typography>
//                         {/* Plugin Cards */}
                        
//                         <Grid container spacing={2}>
//                             {plugins.map((plugin) => {
//                                 const isInstalled = !!installationStatuses[plugin.name];

//                                 return(
//                                 <Grid item xs={12} sm={6} md={4} key={plugin.id} mb={5}>
//                                     <Paper sx={{ padding: 2, borderRadius: "12px", boxShadow: "none", border: "1px solid #E0E0E0", height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
//                                         {/* Plugin Info */}
//                                         <Box>
//                                             <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
//                                                 <img src={plugin.image} alt={plugin.name} style={{ width: "40px", height: "40px" }} />
//                                                 <Typography fontSize="16px" fontFamily="Gilroy-Bold">{plugin.name}</Typography>
//                                             </Box>
//                                             <Typography fontSize="14px" fontFamily="Gilroy-regular" sx={{ mt: 1, mb: 2 }}>
//                                                 {plugin.description}
//                                             </Typography>
//                                         </Box>
//                                         {/* Install Button */}
//                                         <Button
                                               
//                                                 variant="outlined"
//                                                 startIcon={!isInstalled ? <img src="/assets/install-line.svg" alt="install" style={{ width: "16px", filter: "brightness(0)" }} /> : undefined}
//                                                 sx={{
//                                                     alignSelf: 'flex-start', borderRadius: "8px", textTransform: "none", color: "#333", borderColor: "#DADADA", fontFamily: 'Gilroy-Medium',
//                                                     "&:hover": { backgroundColor: "#F5F5F5", borderColor: "#000" },
//                                                     // Styles for disabled (installed) state
//                                                     '&.Mui-disabled': {
//                                                         backgroundColor: 'rgba(0, 0, 0, 0.05)', borderColor: 'rgba(0, 0, 0, 0.1)', color: 'rgba(0, 0, 0, 0.4)', cursor: 'default',
//                                                         '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.05)', borderColor: 'rgba(0, 0, 0, 0.1)' } // Prevent hover effect when disabled
//                                                     },
//                                                 }}
//                                                 // Only allow opening modal if not installed
//                                                 onClick={() => !isInstalled && handleOpenModal(plugin.name)}
//                                                 // Disable button if installed
//                                                 disabled={isInstalled}
//                                             >
//                                                 {/* Change text based on status */}
//                                                 {isInstalled ? "Installed" : "Install"}
//                                             </Button>
//                                          {/* !!! MODAL IS NO LONGER RENDERED HERE INSIDE THE LOOP !!! */}
//                                     </Paper>
//                                 </Grid>)
// })}
//                         </Grid>
//                     </Box>
//                 ))}

//                 {/* No Plugins Found Message */}
//                 {filteredPlugins.length === 0 && (
//                     <Typography fontSize="16px" fontFamily="Gilroy-Bold" sx={{ mt: 2, textAlign: "center" }}>
//                         No plugins found matching your criteria.
//                     </Typography>
//                 )}
//             </Box> {/* End of main page content Container */}

//             {/* ================================================================== */}
//             {/* === SINGLE MODAL INSTANCE (Defined once, outside the loop) === */}
//             {/* Location: Near the end of the ExplorePlugins component's return statement */}
//             {/* ================================================================== */}
//             <Modal
//                 open={openModal}
//                 onClose={handleCloseModal} // Use the handler function for backdrop click/Escape key
//                 aria-labelledby="plugin-modal-title"
//                 closeAfterTransition // Ensures exit transitions complete
//                 slotProps={{
//                     backdrop: {
//                         sx: {
//                             // Optional backdrop styling (blur/dimming)
//                             backdropFilter: 'blur(1px)',
//                             backgroundColor: 'rgba(0, 0, 0, 0.1)',
//                             // Add transition if using theme and wanting smooth blur/fade
//                             // transition: `backdrop-filter ${theme?.transitions?.duration?.shorter}ms ease-in-out, background-color ${theme?.transitions?.duration?.shorter}ms ease-in-out`,
//                         }
//                     }
//                 }}
//             >
//                 {/* Wrapping Box using the defined modalStyle constant */}
//                 <Fade in={openModal} timeout={{ enter: 400, exit: 400 }}>
//                 <Box sx={modalStyle}>
//                      {/* --- Modal Header --- */}
//                     <Box sx={{
//                         display: "flex",
//                         justifyContent: "space-between",
//                         alignItems: "center",
//                         flexShrink: 0, // Prevent header shrinking
//                         px:2,
//                         py:1,
//                     }}>
//                         {/* Dynamic Title based on selectedPlugin */}
//                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
//                      {/* --- MODIFIED: Dynamic Image Source and Alt Text --- */}
//                      {/* Conditionally render the image only if found */}
//                      {currentPluginImage && (
//                          <img
//                             // Use the dynamic image path found above
//                             src={currentPluginImage}
//                             // Make alt text descriptive
//                             alt={selectedPlugin ? `${selectedPlugin} Logo` : 'Plugin Logo'}
//                             style={{ width: '28px', height: '28px', display: 'block' }} // Added display: block for potential alignment improvement
//                         />
//                      )}
//                      {/* Optional: You could show a placeholder if currentPluginImage is null but selectedPlugin exists */}

//                      {/* Dynamic Title based on selectedPlugin */}
//                      <Typography id="plugin-modal-title" fontFamily="Gilroy-SemiBold" fontSize="20px"> {/* Removed p={2} as header box has padding */}
//                         Install {selectedPlugin || "Plugin"}
//                      </Typography>
//                  </Box>
//                         {/* Close Button */}
//                         <IconButton
//                             aria-label="close"
//                             onClick={handleCloseModal} // Use the handler function
//                             sx={{ mr: 1 }} // Margin for spacing
//                         >
//                             <CloseIcon fontSize="small" />
//                         </IconButton>
//                     </Box>

//                     <Divider sx={{ flexShrink: 0 }}/> {/* Divider below header */}

//                      {/* --- Modal Content Area (Scrollable) --- */}
//                     <Box sx={{
//                         // No fixed padding here; handlers add their own padding via Box
//                         overflowY: 'auto', // Make content scrollable if it exceeds max height
//                         flexGrow: 1, // Allow content area to take up available space
//                     }}>
//                         {/* Render the dynamic content component */}
//                         {/* This calls the appropriate function from installHandlers */}
//                         <ModalContent />
//                     </Box>
//                 </Box>
//                 </Fade>
//             </Modal>
//             {/* End of Single Modal Instance */}

//         </Container> // End of main component Box
//     );
// };

// export default ExplorePlugins;
// import React, { useState } from "react";
// import {
//     Box,
//     Button,
//     Container,
//     Grid,
//     Typography,
//     TextField,
//     InputAdornment,
//     Modal,
//     Divider,
//     IconButton,
//     Paper,
// } from "@mui/material";
// import SearchIcon from "@mui/icons-material/Search";
// import { Formik, Form, ErrorMessage } from "formik";
// import * as Yup from "yup";
// import CloseIcon from "@mui/icons-material/Close";
// import { Close } from "@mui/icons-material";
// // Plugin categories
// const categories = [
//     "All",
//     "Cost Agents",
//     "Git Providers",
//     "Kubernetes Stack",
//     // "Policies",
//     // "Cloud Providers",
//     // "IaC",
//     // "Incident Management",
//     // "Project Management",
//     // "Code Quality & Security",
//     // "Feature Management",
//     // "APM & Alerting",
// ];
// // Sample plugin data
// const pluginsData = [
//     { id: 1, name: "GCP Cost Agent", category: "Cost Agents", description: "Lorem ipsum dolor sum it. Lorem ipsum dolor sum it.", image: "/assets/gcp.svg" },
//     { id: 2, name: "AWS Cost Agent", category: "Cost Agents", description: "Lorem ipsum dolor sum it. Lorem ipsum dolor sum it.", image: "/assets/aws.svg" },
//     { id: 3, name: "Azure-devops", category: "Git Providers", description: "Lorem ipsum dolor sum it. Lorem ipsum dolor sum it.", image: "/assets/Azure.svg" },
//     { id: 4, name: "Gitlab", category: "Git Providers", description: "Lorem ipsum dolor sum it. Lorem ipsum dolor sum it.", image: "/assets/Gitlab.svg" },
//     { id: 5, name: "Bitbucket-cloud", category: "Git Providers", description: "Lorem ipsum dolor sum it. Lorem ipsum dolor sum it.", image: "/assets/bit-bucket.svg" },
//     { id: 6, name: "Istio", category: "Kubernetes Stack", description: "Lorem ipsum dolor sum it. Lorem ipsum dolor sum it.", image: "/assets/istio.svg" },
//     { id: 7, name: "Kubernetes", category: "Kubernetes Stack", description: "Lorem ipsum dolor sum it. Lorem ipsum dolor sum it.", image: "/assets/kubernetes.svg" },
//     { id: 8, name: "Kubernetes", category: "Kubernetes Stack", description: "Lorem ipsum dolor sum it. Lorem ipsum dolor sum it.", image: "/assets/kubernetes.svg" },
// ];
// const sectionHeadings: Record<string, string> = {
//     "Git Providers": "Version Control Integrations",
//     "Kubernetes Stack": "Container Orchestration Solutions",
//     "Policies": "Security and Compliance Policies",
//     "Cost Agents": "Cost Optimization Tools",
    
//     // Add more as needed
// };
// const installHandlers = {
//     "GCP Cost Agent": () => (
//         <Formik
//         initialValues={{ projectId: "", datasetId: "", tableId: "" }}
//         validationSchema={Yup.object({
//             projectId: Yup.string().required("Project ID is required"),
//             datasetId: Yup.string().required("Dataset ID is required"),
//             tableId: Yup.string().required("Table ID is required"),
//         })}
//         onSubmit={(values) => {
//             console.log("GCP Cost Agent Submitted Data:", values);
//             //setOpenGCPModal(false); // Optionally close modal on successful submit
//         }}
//     >
//         {({ handleChange, handleBlur, values, errors, touched }) => (
//             // Use the <Form> component rendered by Formik
//             <Form>
//                 {/* === Title Bar Section (Similar to Approve Modal) === */}
//                 <Box display="flex" justifyContent="space-between" alignItems="center">
//                     <Typography fontFamily="Gilroy-SemiBold" fontSize="20px" p={2}> {/* Using style from example */}
//                         Install GCP Cost Agent
//                     </Typography>
//                     {/* <IconButton
//                         edge="end" // Often preferred for close buttons on the right
//                         aria-label="close"
//                         // !!! IMPORTANT: Replace setOpenGCPModal with YOUR state setter for this modal !!!
//                         onClick={() => setOpenGCPModal(false)}
//                         sx={{ mr: 1 }} // Adjust margin as needed (m: 2 also works)
//                     >
//                         <Close fontSize="small" />
//                     </IconButton> */}
//                 </Box>

//                 <Divider /> {/* Divider after title bar */}

//                 {/* === Content Section === */}
//                 <Box p={2}> {/* Wrap content in a Box with padding */}
//                     {/* Service Account Display */}
//                     <Typography fontSize="14px" fontWeight="bold" color="textSecondary">
//                         This is the service account, give this service account the required roles to enable our agent to work on your dataset.
//                     </Typography>
//                     <Box
//                         sx={{
//                             mt: 1, // Margin top
//                             p: 1.5, // Adjusted padding slightly
//                             bgcolor: "#F5F5F5",
//                             borderRadius: "8px",
//                             fontFamily: "monospace",
//                             fontSize: "14px",
//                             wordBreak: "break-all",
//                         }}
//                     >
//                          {/* Ensure Typography is used for text styling consistency */}
//                         <Typography component="span" sx={{ fontFamily: 'monospace', fontSize: '14px' }}>
//                             gcp-cost-agent@your-project.iam.gserviceaccount.com
//                         </Typography>
//                     </Box>
//                 </Box>

//                 <Box px={2}> {/* Wrap fields in a Box with horizontal padding */}
//                     {/* Form Fields */}
//                     <TextField fullWidth variant="filled" name="projectId" label="Project ID"
//                         value={values.projectId} onChange={handleChange} onBlur={handleBlur}
//                         error={!!errors.projectId && touched.projectId} helperText={touched.projectId ? errors.projectId : ""} sx={{ mb: 2 }}
//                     />
//                     <TextField fullWidth variant="filled" name="datasetId" label="Dataset ID"
//                         value={values.datasetId} onChange={handleChange} onBlur={handleBlur}
//                         error={!!errors.datasetId && touched.datasetId} helperText={touched.datasetId ? errors.datasetId : ""} sx={{ mb: 2 }}
//                     />
//                     <TextField fullWidth variant="filled" name="tableId" label="Table ID"
//                         value={values.tableId} onChange={handleChange} onBlur={handleBlur}
//                         error={!!errors.tableId && touched.tableId} helperText={touched.tableId ? errors.tableId : ""} sx={{ mb: 2 }} // Removed extra mb here if button follows directly
//                     />
//                 </Box>

//                 <Divider /> {/* Optional divider before action button */}

//                 {/* === Action Button Section (Similar to Approve Modal) === */}
//                 <Box sx={{ p: 2, textAlign: "right" }}> {/* Use padding and alignment */}
//                     <Button
//                         sx={{
//                             // Borrowing styles from the approve example for consistency
//                             backgroundColor: "black",
//                             color: "white",
//                             fontWeight: "bold",
//                             "&:hover": {
//                                 backgroundColor: "dimgray", // Adjusted hover
//                                 // color: "black",
//                                 // borderColor: "black",
//                             },
//                             textTransform: "none",
//                             px: 4,
//                             fontFamily: "Gilroy-Bold", // Using font from example
//                             fontSize: "14px",
//                             borderRadius: "8px", // Use consistent border radius
//                         }}
//                         variant="contained"
//                         size="small" // Using size from example
//                         type="submit"
//                         // No need for form="gcp-form" if button is inside the <Form> rendered by Formik
//                     >
//                         Install
//                     </Button>
//                 </Box>
//             </Form>
//         )}
//     </Formik>
// ),
//     "AWS Cost Agent": () => <Typography>This is AWS Cost Agent installation.</Typography>,
//     "Azure-devops": () => <Typography>This is Azure DevOps installation.</Typography>,
//     "Gitlab": () => <Typography>This is GitLab installation.</Typography>,
//     "Bitbucket-cloud": () => <Typography>This is Bitbucket installation.</Typography>,
//     "Istio": () => <Typography>This is Istio installation.</Typography>,
//     "Kubernetes": () => <Typography>This is Kubernetes installation.</Typography>,
// };
// const ExplorePlugins: React.FC = () => {
//     const [search, setSearch] = useState("");
//     const [selectedCategory, setSelectedCategory] = useState("All");
//     const [openModal, setOpenModal] = useState(false);
//     const [selectedPlugin, setSelectedPlugin] = useState<string | null>(null);
//     // Filter plugins based on search and selected category
//     const filteredPlugins = pluginsData.filter((plugin) =>
//         (selectedCategory === "All" || plugin.category === selectedCategory) &&
//         plugin.name.toLowerCase().includes(search.toLowerCase())
//     );
//     // Group plugins by category
//     const groupedPlugins = filteredPlugins.reduce((acc, plugin) => {
//         if (!acc[plugin.category]) acc[plugin.category] = [];
//         acc[plugin.category].push(plugin);
//         return acc;
//     }, {} as Record<string, typeof pluginsData>);
//     return (
//         <Box
    
// >
//     {/* Your existing Explore Plugins page content */}

//         <Container maxWidth="lg" sx={{ mt: 4 }}>
//             {/* Header Section */}
//             <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
//                 <Typography fontSize="24px" fontFamily="Gilroy-Bold">Explore Plugins</Typography>
//                 {/* Search Bar */}
//                 <TextField
//                     variant="filled"
//                     placeholder="Search by plugin name..."
//                     value={search}
                   
//                     onChange={(e) => setSearch(e.target.value)}
//                     size="small"
//                     hiddenLabel
//                     sx={{
//                         backgroundColor: "#F5F5F5",
//                         borderRadius: "8px",
//                         width: "300px",
//                     }}
//                     InputProps={{
//                         startAdornment: (
//                            <InputAdornment position="start">
//                                 <img
//                                     src="/assets/search-normal.svg"
//                                     alt="Filter Icon"
//                                     style={{ width: "16px", paddingLeft: "4px" }}
//                                 />
//                             </InputAdornment>
//                         ),
//                         disableUnderline: true,
//                         style: {
//                             backgroundColor: "#f4f4f4",
//                             minHeight: "45px",
//                             width: "300px",
//                             borderRadius: "8px",
//                             fontFamily: "Gilroy-Regular",
//                             fontSize: "14px",
//                         },
//                     }}
//                 />
//             </Box>
//             {/* Category Tabs */}
//             <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 3 }}>
//                 {categories.map((category) => (
//                     <Button
//                         key={category}
//                         onClick={() => setSelectedCategory(category)}
//                         variant="outlined"
//                         sx={{
//                             borderRadius: "20px", // Reduced from 20px
//                             textTransform: "none",
//                             fontWeight: 500,
//                             fontSize: "12px", // Added to reduce text size
//                             padding: "4px 12px", // Reduced padding for smaller button
//                             borderWidth: "1px",
//                             backgroundColor: category === selectedCategory ? "#F5F5F5" : "transparent",
//                             color: "#333",
//                             borderColor: category === selectedCategory ? "#000" : "#DADADA",
//                             "&:hover": {
//                                 backgroundColor: "#EAEAEA",
//                                 borderColor: "#000",
//                             },
//                         }}
//                     >
//                         {category}
//                     </Button>
//                 ))}
//             </Box>
//             {/* Plugin Sections */}
//             {Object.entries(groupedPlugins).map(([category, plugins]) => (
//                 <Box key={category} sx={{ mb: 4 }}>
//                     {/* Section Heading */}
//                     <Typography fontSize="18px" fontWeight="bold" sx={{ mb: 1 }}>{category}</Typography>
//                     <Typography fontSize="14px" fontFamily="Gilroy-regular" sx={{ mb: 2 }}>
//                         {sectionHeadings[category]}
//                     </Typography>
//                     {/* Plugin Cards */}
//                     <Grid container spacing={2}>
//                         {plugins.map((plugin) => (
//                             <Grid item xs={12} sm={6} md={4} key={plugin.id}>
//                                 <Paper sx={{ padding: 2, borderRadius: "12px", boxShadow: "none", border: "1px solid #E0E0E0" }}>
//                                     <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
//                                         <img src={plugin.image} alt={plugin.name} style={{ width: "40px", height: "40px" }} />
//                                         <Typography fontSize="16px" fontFamily="Gilroy-Bold">{plugin.name}</Typography>
//                                     </Box>
//                                     <Typography fontSize="14px" fontFamily="Gilroy-regular" sx={{ mt: 1 }}>
//                                         {plugin.description}
//                                     </Typography>
//                                     <Button
//                                         variant="outlined"
                                        
//                                         startIcon={
//                                             <img
//                                                 src="/assets/install-line.svg"
//                                                 alt="install"
//                                                 style={{ width: "16px", filter: "brightness(0)" }} // Converts the icon to black
//                                             />
//                                         }
//                                         sx={{
//                                             mt: 2,
//                                             borderRadius: "8px",
//                                             textTransform: "none",
//                                             color: "#333", // Text color set to black
//                                             borderColor: "#DADADA", // Light gray border
//                                             "&:hover": {
//                                                 backgroundColor: "#F5F5F5", // Light gray background on hover
//                                                 borderColor: "#000", // Darker border on hover
//                                             }
//                                         }}
//                                         onClick={() => { setSelectedPlugin(plugin.name); setOpenModal(true); }} // Open modal on click
//                                     >
//                                         Install
//                                     </Button>
//                                     {/* Modal */}
//                                     <Modal
//                                                 open={openModal}
//                                                 onClose={(event, reason): any => {
//                                                     // if (reason && reason === "backdropClick") return;
//                                                     // setOpenRejectModal(false);
                                                    
//                                                 }}
//                                                 slotProps={{
//                                                     backdrop: {
//                                                         sx: {
//                                                             // Apply the blur effect to the area behind the backdrop
//                                                             backdropFilter: 'blur(0.5px)', // Adjust the pixel value for more/less blur
//                                                             // Make the backdrop itself transparent so the blur is visible
//                                                             backgroundColor: 'rgba(0, 0, 0, 0)' // Fully transparent
//                                                         }
//                                                     }
//                                                 }}
                                            
//                                             >
                                                
//                                                 <Box
//                                                     sx={{
//                                                         position: "absolute",
//                                                         top: "50%",
//                                                         left: "50%",
//                                                         transform: "translate(-50%, -50%)",
//                                                         width: 700,
//                                                         bgcolor: "white",
//                                                         border: "0px",
//                                                         boxShadow: 2,
//                                                         borderRadius: "16px",
//                                                     }}
//                                                 >
//                                                     {/* <Box display="flex" justifyContent="space-between">
//                                                         <Typography fontFamily="Gilroy-SemiBold" fontSize="20px" p={2}>
//                                                             Approve
//                                                         </Typography>
//                                                         <IconButton
//                                                             edge="start"
//                                                             aria-label="back"
//                                                             color="inherit"
//                                                             onClick={() => setOpenModal(false)}
//                                                             sx={{ m: 2 }}
//                                                         >
//                                                             <Close fontSize="small" />
//                                                         </IconButton>
//                                                     </Box> */}
//                                                     {/* <Divider /> */}
//                                                     {/* <Typography fontSize="16px" p={2}>
//                                                         Are you sure you want to approve the request?
//                                                     </Typography>
//                                                     <Typography fontSize="16px" p={2}>
//                                                         On approving request will be approved and Kubernetes cluster details
//                                                         will be added successful.
//                                                     </Typography>
//                                                     <Divider />
//                                                     <Box sx={{ m: 2, textAlign: "right" }}>
//                                                         <Button
//                                                             sx={{
//                                                                 backgroundColor: "black",
//                                                                 color: "white",
//                                                                 fontWeight: "bold",
//                                                                 "&:hover": {
//                                                                     backgroundColor: "white",
//                                                                     color: "black",
//                                                                     borderColor: "black",
//                                                                 },
//                                                                 textTransform: "none",
//                                                                 px: 4,
//                                                                 fontFamily: "Gilroy-Bold",
//                                                                 fontSize: "14px",
//                                                             }}
//                                                             variant="contained"
//                                                             size="small"
//                                                             type="submit"
//                                                             form="approver-main"
//                                                         >
//                                                             Approve
//                                                         </Button>
//                                                     </Box> */}
//                                                      {selectedPlugin && installHandlers[selectedPlugin as keyof typeof installHandlers]
//                 ? installHandlers[selectedPlugin as keyof typeof installHandlers]()
//                 : <Typography>No Installation Process</Typography>}
//                                                 </Box>
//                                             </Modal>

//                                 </Paper>
//                             </Grid>
//                         ))}
//                     </Grid>
//                 </Box>
//             ))}
//             {/* No Plugins Found Message */}
//             {filteredPlugins.length === 0 && (
//                 <Typography fontSize="16px" fontFamily="Gilroy-Bold" sx={{ mt: 2, textAlign: "center" }}>
//                     No plugins found.
//                 </Typography>
//             )}
//         </Container>
//         </Box>
//     );
// };
// export default ExplorePlugins;